/**
 * ============================================================
 *  JavaScriptCompleteAlgorithms.jsx
 *  ─────────────────────────────────────────────────────────
 *  A single-file React + MUI study guide covering:
 *    • 8 JavaScript Data Structures:
 *        Array, Set, Map, WeakMap, WeakSet, Object,
 *        + custom LinkedList, Stack & Queue
 *    • 6 Algorithm Patterns:
 *        Binary Search, Sorting, Two Pointers,
 *        Sliding Window, Map/Object Patterns,
 *        Recursion & Backtracking
 *
 *  Clean Code Principles applied throughout:
 *    1. Meaningful Names   – variables/functions say WHAT, not HOW
 *    2. Single Responsibility – every component does ONE thing
 *    3. DRY               – shared data in DATA LAYER constants
 *    4. Small Functions   – no function over ~40 lines
 *    5. Comments for WHY  – code shows WHAT, comments explain WHY
 *
 *  For junior devs: Read DATA section first, then COMPONENTS,
 *  then the root APP at the bottom.
 * ============================================================
 */

// ─── React core ──────────────────────────────────────────────
import { useState, useMemo } from "react";

// ─── MUI components ──────────────────────────────────────────
import {
    Box, Drawer, AppBar, Toolbar, Typography, IconButton,
    List, ListItem, ListItemButton, ListItemIcon, ListItemText,
    Divider, Tabs, Tab, Card, CardContent, Chip, Tooltip,
    Accordion, AccordionSummary, AccordionDetails,
    Button, useMediaQuery, CssBaseline, Badge
} from "@mui/material";

// ─── MUI theming ─────────────────────────────────────────────
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";

// ─── MUI icons ───────────────────────────────────────────────
import MenuIcon         from "@mui/icons-material/Menu";
import CloseIcon        from "@mui/icons-material/Close";
import ContentCopyIcon  from "@mui/icons-material/ContentCopy";
import CheckIcon        from "@mui/icons-material/Check";
import ExpandMoreIcon   from "@mui/icons-material/ExpandMore";
import CodeIcon         from "@mui/icons-material/Code";
import SchoolIcon       from "@mui/icons-material/School";
import FunctionsIcon    from "@mui/icons-material/Functions";
import StorageIcon      from "@mui/icons-material/Storage";
import AccountTreeIcon  from "@mui/icons-material/AccountTree";
import LinkIcon         from "@mui/icons-material/Link";
import GridOnIcon       from "@mui/icons-material/GridOn";
import ListAltIcon      from "@mui/icons-material/ListAlt";
import FilterListIcon   from "@mui/icons-material/FilterList";
import LayersIcon       from "@mui/icons-material/Layers";
import SwapVertIcon     from "@mui/icons-material/SwapVert";
import BookmarkIcon     from "@mui/icons-material/Bookmark";
import JavascriptIcon   from "@mui/icons-material/Javascript";

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  SECTION 1 — THEME
//  One source of truth for every colour used in the app.
//  Change a value here → the whole app updates everywhere.
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * APP_COLORS — central colour palette.
 * Using a plain object (not CSS variables) lets us share
 * colours between MUI sx props and the theme config below.
 */
const APP_COLORS = {
    background:   "rgba(16, 7, 32, 0.85)",  // deep purple-black canvas
    surface:      "rgba(26, 14, 52, 0.95)",  // card / panel background
    surfaceHover: "rgba(40, 20, 70, 0.95)",  // card hover state
    border:       "rgba(218, 165, 32, 0.25)",// subtle goldenrod border
    borderHover:  "rgba(218, 165, 32, 0.7)", // goldenrod on hover
    title:        "#DAA520",                 // goldenrod — main headings
    titleBright:  "#FFD700",                 // gold — active / selected
    text:         "#FFFFFF",                 // primary body text
    textMuted:    "rgba(255,255,255,0.55)",  // secondary / hint text
    success:      "#56d364",                 // green  — Easy difficulty
    warning:      "#ffa657",                 // orange — Medium difficulty
    danger:       "#f85149",                 // red    — Hard difficulty
    drawerBg:     "rgba(10, 4, 22, 0.98)",  // sidebar background
    codeBg:       "rgba(6, 2, 14, 0.98)",   // code block background
    jsYellow:     "#f7df1e",                 // JS brand yellow accent
};

/**
 * buildAppTheme — returns a configured MUI dark theme.
 *
 * Why use createTheme instead of writing sx on every component?
 * The theme propagates defaults to ALL MUI components automatically,
 * so we don't repeat ourselves (DRY principle).
 */
const buildAppTheme = () =>
    createTheme({
        palette: {
            mode: "dark",
            background: {
                default: APP_COLORS.background,
                paper:   APP_COLORS.surface,
            },
            primary: { main: APP_COLORS.title },
            text: {
                primary:   APP_COLORS.text,
                secondary: APP_COLORS.textMuted,
            },
        },
        typography: {
            // Monospace font gives the "coding environment" feel
            fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
        },
        components: {
            // Prevent MUI from overriding our rgba background values
            MuiPaper:  { styleOverrides: { root: { backgroundImage: "none" } } },
            MuiDrawer: { styleOverrides: { paper: { backgroundImage: "none" } } },
            MuiAppBar: { styleOverrides: { root:  { backgroundImage: "none" } } },
            MuiCard:   { styleOverrides: { root:  { backgroundImage: "none" } } },
        },
    });

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  SECTION 2 — DATA LAYER
//  All JavaScript content lives here as plain JS objects.
//  UI components only READ this data — they never define code.
//
//  Every topic object follows the same shape:
//  {
//    icon, color, tagline, badge, whenToUse,
//    declare, complexity, note,
//    methods:   [ { name, cat, desc, code } ],
//    questions: [ { title, difficulty,
//                   solutions: [{ label, tip, code }] } ]
//  }
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * CATEGORY_STYLE_MAP — maps a method category to its chip colour.
 * Defined once here; every MethodCard reads from this map.
 */
const CATEGORY_STYLE_MAP = {
    Insert:    { bg: "#0d3321", color: "#56d364" },
    Access:    { bg: "#0d2236", color: "#79c0ff" },
    Modify:    { bg: "#2d2000", color: "#e3b341" },
    Delete:    { bg: "#2d1a1a", color: "#f85149" },
    Info:      { bg: "#1e1333", color: "#bc8cff" },
    Order:     { bg: "#2a1d00", color: "#ffa657" },
    Convert:   { bg: "#002a2d", color: "#39c5cf" },
    Iterate:   { bg: "#1a1a2d", color: "#a5b4fc" },
    Search:    { bg: "#1a2d1a", color: "#86efac" },
    Prototype: { bg: "#2d1a2d", color: "#f9a8d4" },
};

/**
 * DIFFICULTY_COLOR_MAP — colour per difficulty string.
 */
const DIFFICULTY_COLOR_MAP = {
    Easy:   APP_COLORS.success,
    Medium: APP_COLORS.warning,
    Hard:   APP_COLORS.danger,
};

// ─────────────────────────────────────────────────────────────
//  DATA: Array
// ─────────────────────────────────────────────────────────────
const ARRAY_DATA = {
    icon: "📋", color: "#f0883e",
    tagline: "Ordered list — indexed, dynamic length, duplicates allowed",
    badge: "Array (Built-in)",
    whenToUse: "Default choice for ordered collections; index access; iteration",
    declare: "const arr = [];  // or  const arr = new Array();",
    complexity: { get: "O(1)", push: "O(1)*", unshift: "O(n)", search: "O(n)" },
    note: "* O(1) amortised push to end; O(n) insert/delete at front",
    methods: [
        // ── Inserting ─────────────────────────────────────────
        { name: "push(...items)",        cat: "Insert",  desc: "Appends one or more items to the END — returns new length",          code: `const arr = [1, 2, 3];\narr.push(4);       // [1,2,3,4]\narr.push(5, 6);    // [1,2,3,4,5,6]` },
        { name: "unshift(...items)",     cat: "Insert",  desc: "Prepends items to the FRONT — shifts all others right: O(n)",        code: `arr.unshift(0);   // [0,1,2,3]\narr.unshift(-2,-1);// [-2,-1,0,1,2,3]` },
        { name: "splice(i,0,...items)",  cat: "Insert",  desc: "Inserts items at index i without removing any",                      code: `arr.splice(2, 0, 99);\n// inserts 99 at index 2` },

        // ── Accessing ─────────────────────────────────────────
        { name: "arr[index]",            cat: "Access",  desc: "Direct index access — O(1); returns undefined if out of bounds",     code: `const first = arr[0];   // O(1)\nconst last  = arr[arr.length - 1];` },
        { name: "at(index)",             cat: "Access",  desc: "Like arr[i] but supports NEGATIVE indices from the end",             code: `arr.at(-1);  // last element\narr.at(-2);  // second to last` },
        { name: "slice(start, end)",     cat: "Access",  desc: "Returns shallow copy of portion [start, end) — non-destructive",    code: `const arr = [1,2,3,4,5];\narr.slice(1, 3);   // [2,3]\narr.slice(-2);     // [4,5]` },

        // ── Modifying ─────────────────────────────────────────
        { name: "splice(i, n, ...new)",  cat: "Modify",  desc: "Removes n items at i, optionally inserts replacements — mutates",   code: `const arr = [1,2,3,4,5];\narr.splice(1, 2, 'a','b');\n// [1,'a','b',4,5]` },
        { name: "fill(val, s, e)",       cat: "Modify",  desc: "Fills elements from index s to e with val — mutates",               code: `[0,0,0,0].fill(7, 1, 3);\n// [0,7,7,0]` },
        { name: "reverse()",             cat: "Modify",  desc: "Reverses array IN PLACE — mutates the original",                    code: `[1,2,3].reverse();\n// [3,2,1]  ← original changed!` },
        { name: "sort(compareFn)",       cat: "Order",   desc: "Sorts in-place. Default: lexicographic. Provide fn for numbers",    code: `// ⚠️ Default sorts as STRINGS:\n[10,9,2].sort()         // [10,2,9] WRONG!\n// ✅ Correct numeric sort:\n[10,9,2].sort((a,b) => a - b) // [2,9,10]` },

        // ── Deleting ──────────────────────────────────────────
        { name: "pop()",                 cat: "Delete",  desc: "Removes and returns the LAST element — O(1)",                       code: `const arr = [1,2,3];\nconst last = arr.pop(); // 3\n// arr is now [1,2]` },
        { name: "shift()",               cat: "Delete",  desc: "Removes and returns the FIRST element — O(n) due to re-indexing",   code: `const first = arr.shift(); // 1\n// arr is now [2,3]` },
        { name: "splice(i, n)",          cat: "Delete",  desc: "Removes n elements starting at index i — returns removed items",    code: `const arr = [1,2,3,4,5];\nconst removed = arr.splice(1, 2);\n// removed=[2,3]  arr=[1,4,5]` },

        // ── Info / Search ──────────────────────────────────────
        { name: "length",                cat: "Info",    desc: "Property (not method) returning number of elements",                 code: `[1,2,3].length; // 3\n// Setting length truncates or pads:\nconst a = [1,2,3];\na.length = 2; // [1,2]` },
        { name: "indexOf(val)",          cat: "Search",  desc: "Returns first index of val, or -1 — uses strict equality ===",      code: `[1,2,3,2].indexOf(2);    // 1\n[1,2,3,2].lastIndexOf(2); // 3\n[1,2,3].indexOf(99);      // -1` },
        { name: "includes(val)",         cat: "Search",  desc: "Returns true/false — cleaner than indexOf for existence checks",    code: `[1,2,3].includes(2);  // true\n[1,2,3].includes(99); // false\n// ✅ Also handles NaN (indexOf cannot):\n[NaN].includes(NaN);  // true` },
        { name: "findIndex(fn)",         cat: "Search",  desc: "Returns index of FIRST element satisfying the predicate",           code: `[5,12,8,130].findIndex(x => x > 10);\n// 1  (12 is at index 1)` },
        { name: "find(fn)",              cat: "Search",  desc: "Returns the FIRST element satisfying the predicate, or undefined",  code: `[5,12,8,130].find(x => x > 10);\n// 12` },

        // ── Iterating ─────────────────────────────────────────
        { name: "forEach(fn)",           cat: "Iterate", desc: "Runs fn(item,index,arr) for each element — returns undefined",      code: `[1,2,3].forEach((val, idx) => {\n  console.log(idx, val);\n});\n// 0 1 / 1 2 / 2 3` },
        { name: "map(fn)",               cat: "Iterate", desc: "Returns NEW array of fn(item) results — non-destructive",           code: `[1,2,3].map(x => x * 2);\n// [2,4,6]  ← new array` },
        { name: "filter(fn)",            cat: "Iterate", desc: "Returns NEW array of elements where fn returns truthy",             code: `[1,2,3,4,5].filter(x => x % 2 === 0);\n// [2,4]` },
        { name: "reduce(fn, init)",      cat: "Iterate", desc: "Accumulates array into a single value — very powerful",            code: `[1,2,3,4].reduce((acc, x) => acc + x, 0);\n// 10\n// acc starts at init=0 and grows` },
        { name: "some(fn)",              cat: "Info",    desc: "Returns true if AT LEAST ONE element satisfies fn",                 code: `[1,2,3].some(x => x > 2);   // true\n[1,2,3].some(x => x > 9);   // false` },
        { name: "every(fn)",             cat: "Info",    desc: "Returns true only if ALL elements satisfy fn",                      code: `[2,4,6].every(x => x % 2 === 0); // true\n[2,3,6].every(x => x % 2 === 0); // false` },

        // ── Converting ────────────────────────────────────────
        { name: "join(separator)",       cat: "Convert", desc: "Joins all elements into a string with given separator",             code: `['a','b','c'].join('-'); // "a-b-c"\n[1,2,3].join('');          // "123"` },
        { name: "flat(depth)",           cat: "Convert", desc: "Flattens nested arrays up to depth levels",                        code: `[1,[2,[3,[4]]]].flat();     // [1,2,[3,[4]]]\n[1,[2,[3,[4]]]].flat(Infinity); // [1,2,3,4]` },
        { name: "flatMap(fn)",           cat: "Convert", desc: "map() then flat(1) in one pass — very efficient",                  code: `[1,2,3].flatMap(x => [x, x*2]);\n// [1,2,2,4,3,6]` },
        { name: "Array.from(iterable)",  cat: "Convert", desc: "Creates array from any iterable or array-like object",             code: `Array.from('hello');         // ['h','e','l','l','o']\nArray.from({length:3}, (_,i)=>i); // [0,1,2]` },
        { name: "spread [...arr]",       cat: "Convert", desc: "Spreads elements — useful for copy or combining arrays",           code: `const copy    = [...arr];\nconst combined = [...a, ...b];\n// Shallow copy only!` },
    ],
    questions: [
        {
            title: "Find all duplicates in an Array",
            difficulty: "Easy",
            solutions: [
                {
                    label: "🔵 Set + filter",
                    tip: "Set stores unique values; filter keeps elements seen more than once.",
                    code: `const nums = [1, 2, 3, 2, 4, 3, 5];

// Count occurrences with reduce into a Map
const count = nums.reduce((map, n) => {
  map.set(n, (map.get(n) || 0) + 1);
  return map;
}, new Map());

// Keep only items that appeared more than once
const duplicates = [...new Set(
  nums.filter(n => count.get(n) > 1)
)];

console.log(duplicates); // [2, 3]`,
                },
                {
                    label: "🟠 indexOf vs lastIndexOf",
                    tip: "If first and last index differ, the value appears more than once.",
                    code: `const nums = [1, 2, 3, 2, 4, 3, 5];

const duplicates = nums.filter((val, idx) =>
  // A duplicate has different first and last positions
  nums.indexOf(val) !== nums.lastIndexOf(val) &&
  nums.indexOf(val) === idx // avoid adding the same dup twice
);

console.log(duplicates); // [2, 3]`,
                },
                {
                    label: "🟢 Sort + adjacent compare",
                    tip: "After sorting, duplicates sit next to each other — O(n log n).",
                    code: `const nums = [1, 2, 3, 2, 4, 3, 5];

const sorted = [...nums].sort((a, b) => a - b);
const duplicates = [];

for (let i = 1; i < sorted.length; i++) {
  // Adjacent equal elements means a duplicate
  if (sorted[i] === sorted[i - 1] &&
      !duplicates.includes(sorted[i])) {
    duplicates.push(sorted[i]);
  }
}

console.log(duplicates); // [2, 3]`,
                },
            ],
        },
        {
            title: "Rotate an array by k steps",
            difficulty: "Medium",
            solutions: [
                {
                    label: "🔵 slice + concat",
                    tip: "Use modulo to handle k > length. slice creates two parts, concat joins them.",
                    code: `function rotateRight(arr, k) {
  const n = arr.length;
  // Modulo handles k larger than array length
  const steps = k % n;
  if (steps === 0) return arr;

  // Take last 'steps' items, put them at front
  return [
    ...arr.slice(n - steps),
    ...arr.slice(0, n - steps)
  ];
}

console.log(rotateRight([1,2,3,4,5], 2));
// [4,5,1,2,3]`,
                },
                {
                    label: "🟠 Reverse three times",
                    tip: "Classic O(1) space trick: reverse all, then reverse each part.",
                    code: `function rotateRight(arr, k) {
  const n = arr.length;
  k = k % n;

  const reverse = (a, l, r) => {
    while (l < r) {
      [a[l], a[r]] = [a[r], a[l]]; // destructure swap
      l++; r--;
    }
  };

  // Step 1: reverse entire array
  reverse(arr, 0, n - 1);
  // Step 2: reverse first k elements
  reverse(arr, 0, k - 1);
  // Step 3: reverse the remaining
  reverse(arr, k, n - 1);

  return arr;
}`,
                },
                {
                    label: "🟢 pop + unshift loop",
                    tip: "Most readable. O(n*k) — fine for small k.",
                    code: `function rotateRight(arr, k) {
  const steps = k % arr.length;

  for (let i = 0; i < steps; i++) {
    // Remove last element and put it at front
    const last = arr.pop();
    arr.unshift(last);
  }
  return arr;
}

console.log(rotateRight([1,2,3,4,5], 2));
// [4,5,1,2,3]`,
                },
            ],
        },
        {
            title: "Flatten a deeply nested array",
            difficulty: "Medium",
            solutions: [
                {
                    label: "🔵 flat(Infinity)",
                    tip: "Built-in — always try this first in real code.",
                    code: `const nested = [1, [2, [3, [4, [5]]]]];

const flat = nested.flat(Infinity);
console.log(flat); // [1,2,3,4,5]

// Flat to a specific depth:
nested.flat(2); // [1,2,3,[4,[5]]]`,
                },
                {
                    label: "🟠 Recursive reduce",
                    tip: "Classic recursive approach — great for understanding the concept.",
                    code: `function deepFlatten(arr) {
  return arr.reduce((flat, item) => {
    // If item is an array, recursively flatten it
    // Otherwise just add the item as-is
    return flat.concat(
      Array.isArray(item) ? deepFlatten(item) : item
    );
  }, []);
}

console.log(deepFlatten([1,[2,[3,[4]]]]));
// [1,2,3,4]`,
                },
                {
                    label: "🟢 Stack-based iterative",
                    tip: "O(n) no call-stack overflow risk for deeply nested arrays.",
                    code: `function deepFlatten(arr) {
  const stack = [...arr]; // copy so we don't mutate input
  const result = [];

  while (stack.length) {
    const item = stack.pop();
    if (Array.isArray(item)) {
      // Push children back onto stack to process
      stack.push(...item);
    } else {
      result.unshift(item); // unshift preserves order
    }
  }
  return result;
}`,
                },
            ],
        },
    ],
};

// ─────────────────────────────────────────────────────────────
//  DATA: Set
// ─────────────────────────────────────────────────────────────
const SET_DATA = {
    icon: "🔷", color: "#58a6ff",
    tagline: "Unique values — no duplicates, insertion order preserved",
    badge: "Set (Built-in ES6)",
    whenToUse: "Uniqueness checks, deduplication, fast O(1) has() lookups",
    declare: "const set = new Set();  // or  new Set([1, 2, 3])",
    complexity: { has: "O(1)", add: "O(1)", delete: "O(1)", iterate: "O(n)" },
    note: "Unlike Java's HashSet, JS Set preserves insertion order during iteration",
    methods: [
        { name: "add(value)",          cat: "Insert",  desc: "Adds value; silently ignores duplicates — returns the Set",           code: `const s = new Set();\ns.add(1).add(2).add(2); // chaining works!\n// Set {1, 2} — 2 was ignored` },
        { name: "has(value)",          cat: "Search",  desc: "Returns true if value exists — O(1) hash lookup",                     code: `const s = new Set([1,2,3]);\ns.has(2);  // true\ns.has(99); // false` },
        { name: "delete(value)",       cat: "Delete",  desc: "Removes value; returns true if found, false otherwise",               code: `const s = new Set([1,2,3]);\ns.delete(2); // true\ns.delete(9); // false (wasn't there)` },
        { name: "clear()",             cat: "Delete",  desc: "Removes ALL values — empties the set",                                code: `const s = new Set([1,2,3]);\ns.clear();\nconsole.log(s.size); // 0` },
        { name: "size",                cat: "Info",    desc: "Property (not method) — count of unique values",                      code: `const s = new Set([1,2,2,3]);\nconsole.log(s.size); // 3 (not 4!)` },
        { name: "forEach(fn)",         cat: "Iterate", desc: "Iterates values in insertion order",                                  code: `new Set(['a','b','c']).forEach(val => {\n  console.log(val);\n});\n// a  b  c  (insertion order)` },
        { name: "for...of",            cat: "Iterate", desc: "ES6 iterator — cleanest way to loop",                                 code: `const s = new Set([10,20,30]);\nfor (const val of s) {\n  console.log(val);\n}` },
        { name: "values()",            cat: "Iterate", desc: "Returns an iterator of all values",                                   code: `const iter = new Set([1,2,3]).values();\nconsole.log([...iter]); // [1,2,3]` },
        { name: "keys()",              cat: "Iterate", desc: "Same as values() — Sets have no keys, alias exists for Map compat",   code: `// In a Set, keys() === values()\nconst s = new Set([1,2,3]);\n[...s.keys()]; // [1,2,3]` },
        { name: "entries()",           cat: "Iterate", desc: "Returns [value, value] pairs — for Map API compatibility",            code: `for (const [k, v] of new Set([1,2]).entries()) {\n  console.log(k, v); // 1 1 / 2 2\n}` },
        { name: "spread [...set]",     cat: "Convert", desc: "Converts Set to Array — needed for array methods",                   code: `const s = new Set([3,1,2]);\nconst arr = [...s]; // [3,1,2]\n// Now you can sort, map, filter etc.` },
        { name: "Array.from(set)",     cat: "Convert", desc: "Same result as spread — slightly more explicit",                     code: `const arr = Array.from(new Set([3,1,2]));\nconsole.log(arr); // [3,1,2]` },
    ],
    questions: [
        {
            title: "Remove duplicates from an Array (preserve order)",
            difficulty: "Easy",
            solutions: [
                {
                    label: "🔵 new Set() + spread",
                    tip: "One-liner — JS Set automatically ignores duplicates AND keeps insertion order.",
                    code: `const arr = ['banana','apple','cherry','apple','banana','date'];

// Set constructor removes dups; spread converts back to array
const unique = [...new Set(arr)];

console.log(unique);
// ['banana','apple','cherry','date'] ✅ order preserved`,
                },
                {
                    label: "🟠 filter + indexOf",
                    tip: "Keep only the first occurrence (where indexOf finds the same index).",
                    code: `const arr = ['banana','apple','cherry','apple','banana','date'];

const unique = arr.filter(
  // Keep item only if this is its FIRST occurrence
  (item, index) => arr.indexOf(item) === index
);

console.log(unique);
// ['banana','apple','cherry','date']`,
                },
                {
                    label: "🟢 reduce into Set",
                    tip: "Shows the reduce pattern — useful when you need more control.",
                    code: `const arr = ['banana','apple','cherry','apple','banana','date'];

const unique = [...arr.reduce(
  (set, item) => set.add(item),
  new Set()
)];

console.log(unique);
// ['banana','apple','cherry','date']`,
                },
            ],
        },
        {
            title: "Find intersection of two arrays",
            difficulty: "Easy",
            solutions: [
                {
                    label: "🔵 Set + filter",
                    tip: "Convert one array to Set for O(1) lookup; filter the other.",
                    code: `const a = [1, 2, 3, 4, 5];
const b = [3, 4, 5, 6, 7];

// Put b in a Set for O(1) lookup
const setB = new Set(b);

// Keep only items from a that exist in setB
const intersection = a.filter(x => setB.has(x));

console.log(intersection); // [3,4,5]`,
                },
                {
                    label: "🟠 Two Set operations",
                    tip: "Classic set theory: keep elements present in BOTH sets.",
                    code: `const setA = new Set([1,2,3,4,5]);
const setB = new Set([3,4,5,6,7]);

// Filter setA keeping only elements that setB also has
const intersection = new Set(
  [...setA].filter(x => setB.has(x))
);

console.log([...intersection]); // [3,4,5]`,
                },
                {
                    label: "🟢 Manual loop",
                    tip: "Explicit and easy to debug — good for interviews.",
                    code: `const a = [1,2,3,4,5];
const b = new Set([3,4,5,6,7]);

const intersection = [];
for (const val of a) {
  if (b.has(val)) intersection.push(val);
}

console.log(intersection); // [3,4,5]`,
                },
            ],
        },
        {
            title: "Check if array has all unique values",
            difficulty: "Easy",
            solutions: [
                {
                    label: "🔵 Set size comparison",
                    tip: "If Set size equals array length, no duplicates existed.",
                    code: `function allUnique(arr) {
  // Set removes duplicates — if size drops, there were dups
  return new Set(arr).size === arr.length;
}

console.log(allUnique([1,2,3,4]));   // true
console.log(allUnique([1,2,2,4]));   // false`,
                },
                {
                    label: "🟠 some + indexOf",
                    tip: "Short-circuits early when first duplicate found.",
                    code: `function allUnique(arr) {
  // some() stops as soon as it finds a duplicate
  return !arr.some(
    (item, index) => arr.indexOf(item) !== index
  );
}

console.log(allUnique([1,2,3,4]));   // true
console.log(allUnique([1,2,2,4]));   // false`,
                },
                {
                    label: "🟢 Manual Set loop",
                    tip: "Most explicit — easy to extend (e.g., return the duplicate).",
                    code: `function allUnique(arr) {
  const seen = new Set();
  for (const item of arr) {
    if (seen.has(item)) return false; // early exit!
    seen.add(item);
  }
  return true;
}

console.log(allUnique([1,2,3,4]));   // true
console.log(allUnique([1,2,2,4]));   // false`,
                },
            ],
        },
    ],
};

// ─────────────────────────────────────────────────────────────
//  DATA: Map
// ─────────────────────────────────────────────────────────────
const MAP_DATA = {
    icon: "🗺️", color: "#7ee787",
    tagline: "Key→Value pairs — any type as key, insertion order preserved",
    badge: "Map (Built-in ES6)",
    whenToUse: "Key-value storage with non-string keys; ordered iteration; size property",
    declare: "const map = new Map();  // or  new Map([[key,val],...])",
    complexity: { get: "O(1)", set: "O(1)", has: "O(1)", delete: "O(1)" },
    note: "Unlike Object, Map allows ANY value as key (objects, functions, NaN)",
    methods: [
        { name: "set(key, value)",     cat: "Insert",  desc: "Adds or updates a key-value pair — returns the Map (chainable)",     code: `const map = new Map();\nmap.set('name', 'Alice')\n   .set('age',  30);       // chaining!\n// Map { name→Alice, age→30 }` },
        { name: "get(key)",            cat: "Access",  desc: "Returns value for key, or undefined if key is absent",               code: `const map = new Map([['a',1],['b',2]]);\nmap.get('a');  // 1\nmap.get('z');  // undefined` },
        { name: "has(key)",            cat: "Search",  desc: "Returns true if the key exists — O(1)",                              code: `map.has('a');  // true\nmap.has('z');  // false` },
        { name: "delete(key)",         cat: "Delete",  desc: "Removes the mapping; returns true if it existed",                    code: `map.delete('b'); // true\nmap.delete('z'); // false` },
        { name: "clear()",             cat: "Delete",  desc: "Removes all key-value pairs from the map",                           code: `map.clear();\nconsole.log(map.size); // 0` },
        { name: "size",                cat: "Info",    desc: "Property — number of key-value pairs",                               code: `const map = new Map([['a',1],['b',2]]);\nconsole.log(map.size); // 2` },
        { name: "keys()",              cat: "Iterate", desc: "Returns iterator of all keys in insertion order",                    code: `for (const key of map.keys()) {\n  console.log(key);\n}` },
        { name: "values()",            cat: "Iterate", desc: "Returns iterator of all values in insertion order",                  code: `for (const val of map.values()) {\n  console.log(val);\n}` },
        { name: "entries()",           cat: "Iterate", desc: "Returns [key, value] iterator — default for for...of",               code: `for (const [k, v] of map) { // entries() is default\n  console.log(k, '→', v);\n}` },
        { name: "forEach(fn)",         cat: "Iterate", desc: "Calls fn(value, key, map) for each pair in order",                   code: `map.forEach((value, key) => {\n  // NOTE: value comes BEFORE key (unlike for...of)\n  console.log(key, value);\n});` },
        { name: "Map from Object",     cat: "Convert", desc: "Convert a plain object to a Map",                                    code: `const obj = { a:1, b:2, c:3 };\nconst map = new Map(Object.entries(obj));\n// Map { a→1, b→2, c→3 }` },
        { name: "Map to Object",       cat: "Convert", desc: "Convert a Map back to a plain object",                               code: `const obj = Object.fromEntries(map);\n// { a:1, b:2, c:3 }` },
    ],
    questions: [
        {
            title: "Count character frequency in a string",
            difficulty: "Easy",
            solutions: [
                {
                    label: "🔵 Map.get() || 0",
                    tip: "get() returns undefined when key missing; || 0 gives us a safe starting count.",
                    code: `function charFrequency(str) {
  const freq = new Map();

  for (const char of str) {
    // If char not in map, start at 0 then add 1
    freq.set(char, (freq.get(char) || 0) + 1);
  }
  return freq;
}

const result = charFrequency('hello world');
console.log([...result.entries()]);
// [['h',1],['e',1],['l',3],['o',2],[' ',1],['w',1],['r',1],['d',1]]`,
                },
                {
                    label: "🟠 reduce into Map",
                    tip: "Functional style — no explicit loop.",
                    code: `function charFrequency(str) {
  return [...str].reduce((map, char) => {
    map.set(char, (map.get(char) || 0) + 1);
    return map;
  }, new Map());
}

const result = charFrequency('mississippi');
result.get('s'); // 4`,
                },
                {
                    label: "🟢 Map + spread sort",
                    tip: "Count then sort by frequency for a ranked result.",
                    code: `function topChars(str) {
  const freq = new Map();
  for (const c of str)
    freq.set(c, (freq.get(c) || 0) + 1);

  // Sort entries by count descending
  return [...freq.entries()]
    .sort((a, b) => b[1] - a[1]);
}

topChars('mississippi');
// [['s',4],['i',4],['p',2],['m',1]]`,
                },
            ],
        },
        {
            title: "Two Sum — unsorted array", difficulty: "Easy",
            solutions: [
                {
                    label: "🔵 Map complement lookup",
                    tip: "Store value→index in Map. For each number, check if its complement exists already.",
                    code: `function twoSum(nums, target) {
  // map: value → its index
  const seen = new Map();

  for (let i = 0; i < nums.length; i++) {
    const complement = target - nums[i];

    // Have we already passed the number we need?
    if (seen.has(complement)) {
      return [seen.get(complement), i];
    }
    seen.set(nums[i], i);
  }
  return []; // no solution found
}

twoSum([2, 7, 11, 15], 9); // [0, 1]`,
                },
                {
                    label: "🟠 Object as lookup table",
                    tip: "Same idea but with a plain object — slightly faster for string keys.",
                    code: `function twoSum(nums, target) {
  const seen = {}; // { value: index }

  for (let i = 0; i < nums.length; i++) {
    const complement = target - nums[i];

    if (complement in seen) {
      return [seen[complement], i];
    }
    seen[nums[i]] = i;
  }
  return [];
}`,
                },
                {
                    label: "🟢 Brute force O(n²)",
                    tip: "Nested loops — simple to understand but too slow for large inputs.",
                    code: `function twoSum(nums, target) {
  for (let i = 0; i < nums.length; i++) {
    for (let j = i + 1; j < nums.length; j++) {
      if (nums[i] + nums[j] === target) {
        return [i, j];
      }
    }
  }
  return [];
}
// O(n²) — only use for n < 1000`,
                },
            ],
        },
        {
            title: "Group anagrams together", difficulty: "Medium",
            solutions: [
                {
                    label: "🔵 Sorted key in Map",
                    tip: "Sort each word's letters — anagrams produce the same sorted key.",
                    code: `function groupAnagrams(words) {
  const groups = new Map();

  for (const word of words) {
    // Sorting letters gives the canonical key
    // 'eat' → 'aet',  'tea' → 'aet'  (same key!)
    const key = word.split('').sort().join('');

    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(word);
  }

  return [...groups.values()];
}

groupAnagrams(['eat','tea','tan','ate','nat','bat']);
// [['eat','tea','ate'],['tan','nat'],['bat']]`,
                },
                {
                    label: "🟠 Char count key",
                    tip: "Encode char frequency as a string key — avoids sorting: O(n*26) vs O(n*k*log k).",
                    code: `function groupAnagrams(words) {
  const groups = new Map();

  for (const word of words) {
    // Build a 26-slot frequency array for a-z
    const count = new Array(26).fill(0);
    for (const c of word) {
      count[c.charCodeAt(0) - 97]++;
    }
    // Convert to string: "1,0,0,...,1,0,..." as unique key
    const key = count.join(',');

    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(word);
  }

  return [...groups.values()];
}`,
                },
                {
                    label: "🟢 Object.groupBy (ES2024)",
                    tip: "Newest browser API — check compatibility before using in production.",
                    code: `// ES2024 native groupBy (Chrome 117+, Node 21+)
function groupAnagrams(words) {
  return Object.values(
    Object.groupBy(words, word =>
      word.split('').sort().join('')
    )
  );
}

// Polyfill for older environments:
// words.reduce((acc, w) => {
//   const k = w.split('').sort().join('');
//   (acc[k] ??= []).push(w);
//   return acc;
// }, {})`,
                },
            ],
        },
    ],
};

// ─────────────────────────────────────────────────────────────
//  DATA: WeakMap
// ─────────────────────────────────────────────────────────────
const WEAK_MAP_DATA = {
    icon: "🌫️", color: "#a5b4fc",
    tagline: "Object-keyed map — keys are weakly held (garbage collectible)",
    badge: "WeakMap (Built-in ES6)",
    whenToUse: "Private instance data, caching object metadata without memory leaks",
    declare: "const wm = new WeakMap();",
    complexity: { get: "O(1)", set: "O(1)", has: "O(1)", delete: "O(1)" },
    note: "Keys MUST be objects (or registered symbols). Not iterable. No size property.",
    methods: [
        { name: "set(obj, value)",    cat: "Insert",  desc: "Associates value with obj key — obj must be an object",            code: `const wm = new WeakMap();\nconst key = {}; // must be an object!\nwm.set(key, 'secret data');` },
        { name: "get(obj)",           cat: "Access",  desc: "Returns associated value, or undefined if not found",              code: `const key = {};\nconst wm = new WeakMap();\nwm.set(key, 42);\nwm.get(key);  // 42\nwm.get({});   // undefined (different object!)` },
        { name: "has(obj)",           cat: "Search",  desc: "Returns true if obj is a key in the WeakMap",                     code: `wm.has(key);  // true\nwm.has({});   // false (different reference)` },
        { name: "delete(obj)",        cat: "Delete",  desc: "Removes the entry for obj — returns true if it existed",          code: `wm.delete(key); // true\n// When key goes out of scope,\n// GC can clean it up automatically` },
        { name: "Private class data", cat: "Prototype", desc: "Classic WeakMap use: store private data keyed by instance",     code: `const _private = new WeakMap();\n\nclass Person {\n  constructor(name) {\n    // Store private data outside the object\n    _private.set(this, { name });\n  }\n  greet() {\n    return \`Hi, I'm \${_private.get(this).name}\`;\n  }\n}` },
        { name: "Caching / memoize", cat: "Prototype", desc: "Cache expensive computation results linked to object lifetime",  code: `const cache = new WeakMap();\n\nfunction expensiveCalc(obj) {\n  if (cache.has(obj)) return cache.get(obj);\n  const result = /* heavy work */ obj.value * 2;\n  cache.set(obj, result); // auto-cleared when obj dies\n  return result;\n}` },
    ],
    questions: [
        {
            title: "Implement private class fields using WeakMap", difficulty: "Medium",
            solutions: [
                {
                    label: "🔵 WeakMap per field",
                    tip: "WeakMap key is 'this' — inaccessible outside class, no memory leak.",
                    code: `// One WeakMap per private field
const _name = new WeakMap();
const _age  = new WeakMap();

class User {
  constructor(name, age) {
    _name.set(this, name);
    _age.set(this, age);
  }

  getInfo() {
    // Only methods inside this closure can access _name/_age
    return \`\${_name.get(this)}, age \${_age.get(this)}\`;
  }
}

const user = new User('Alice', 30);
console.log(user.getInfo()); // "Alice, age 30"
console.log(user._name);     // undefined — truly private!`,
                },
                {
                    label: "🟠 Single WeakMap with object value",
                    tip: "Store all private fields in one WeakMap as a plain object — cleaner.",
                    code: `const _data = new WeakMap();

class BankAccount {
  constructor(owner, balance) {
    // Store all private state in one map entry
    _data.set(this, { owner, balance });
  }

  deposit(amount) {
    const data = _data.get(this);
    data.balance += amount;
  }

  getBalance() {
    return _data.get(this).balance;
  }
}

const acc = new BankAccount('Bob', 1000);
acc.deposit(500);
console.log(acc.getBalance()); // 1500`,
                },
                {
                    label: "🟢 Modern # private fields (ES2022)",
                    tip: "Prefer native # syntax today — WeakMap approach is the legacy pattern.",
                    code: `// ✅ Modern ES2022 native private fields
class User {
  #name; // truly private — enforced by the engine
  #age;

  constructor(name, age) {
    this.#name = name;
    this.#age  = age;
  }

  getInfo() {
    return \`\${this.#name}, age \${this.#age}\`;
  }
}

const u = new User('Alice', 30);
console.log(u.getInfo()); // "Alice, age 30"
// u.#name → SyntaxError! Cannot access outside class`,
                },
            ],
        },
        {
            title: "Memoize a function without memory leaks", difficulty: "Medium",
            solutions: [
                {
                    label: "🔵 WeakMap cache",
                    tip: "When the input object is GC'd, the cached result is too — no leak!",
                    code: `function memoize(fn) {
  // WeakMap: key = input object, value = cached result
  const cache = new WeakMap();

  return function(obj) {
    if (cache.has(obj)) {
      console.log('cache hit!');
      return cache.get(obj);
    }
    const result = fn(obj);
    cache.set(obj, result);
    return result;
  };
}

const expensive = memoize(obj => obj.x * obj.y * 1000);

const data = { x: 3, y: 4 };
expensive(data); // computed: 12000
expensive(data); // cache hit! → 12000`,
                },
                {
                    label: "🟠 Map cache (comparison)",
                    tip: "Map works too but holds strong references — potential memory leak.",
                    code: `function memoize(fn) {
  // ⚠️ Map holds STRONG references — objects won't be GC'd!
  const cache = new Map();

  return function(obj) {
    if (cache.has(obj)) return cache.get(obj);
    const result = fn(obj);
    cache.set(obj, result);
    return result;
  };
}
// When to use Map cache instead of WeakMap:
// → When keys are primitive (strings, numbers)
// → When you WANT to control cache lifetime manually`,
                },
                {
                    label: "🟢 Decorator pattern (ES2023+)",
                    tip: "Memoize as a method decorator — clean and reusable.",
                    code: `// Decorator-style memoize utility
function memoized(target, key, descriptor) {
  const fn = descriptor.value;
  const cache = new WeakMap();

  descriptor.value = function(...args) {
    if (!cache.has(this)) cache.set(this, new Map());
    const methodCache = cache.get(this);
    const cacheKey = JSON.stringify(args);

    if (methodCache.has(cacheKey))
      return methodCache.get(cacheKey);

    const result = fn.apply(this, args);
    methodCache.set(cacheKey, result);
    return result;
  };
  return descriptor;
}`,
                },
            ],
        },
        {
            title: "Track DOM element metadata without leaks", difficulty: "Easy",
            solutions: [
                {
                    label: "🔵 WeakMap for DOM data",
                    tip: "When the DOM node is removed, WeakMap entry is automatically GC'd.",
                    code: `const elementData = new WeakMap();

function attachData(element, data) {
  elementData.set(element, data);
}

function getData(element) {
  return elementData.get(element) || {};
}

// Usage:
const btn = document.createElement('button');
attachData(btn, { clicks: 0, label: 'Submit' });

// When btn is removed from DOM and loses all references,
// elementData entry is automatically garbage collected ✅`,
                },
                {
                    label: "🟠 data- attributes (alternative)",
                    tip: "HTML data attributes — simpler but only stores strings.",
                    code: `// Alternative: HTML data attributes
const btn = document.querySelector('button');

// Store (strings only!)
btn.dataset.clicks = 0;
btn.dataset.label  = 'Submit';

// Read
console.log(btn.dataset.clicks); // "0" (always string!)
console.log(+btn.dataset.clicks); // 0 (parsed)

// ⚠️ Vs WeakMap:
//   data- attributes: visible in DevTools, strings only
//   WeakMap: private, any type, auto-cleaned`,
                },
                {
                    label: "🟢 Symbol property",
                    tip: "Symbol keys are non-enumerable — semi-private but not truly hidden.",
                    code: `const META = Symbol('meta'); // unique key

const btn = document.createElement('button');
btn[META] = { clicks: 0, created: Date.now() };

// Read
console.log(btn[META].clicks); // 0

// Semi-private: won't show in for...in or Object.keys()
// BUT: Object.getOwnPropertySymbols() can find it
// WeakMap is MORE private and doesn't pollute the object`,
                },
            ],
        },
    ],
};

// ─────────────────────────────────────────────────────────────
//  DATA: WeakSet
// ─────────────────────────────────────────────────────────────
const WEAK_SET_DATA = {
    icon: "🌐", color: "#f9a8d4",
    tagline: "Set of weakly-held object references — auto garbage collected",
    badge: "WeakSet (Built-in ES6)",
    whenToUse: "Tracking object membership (visited, processed) without preventing GC",
    declare: "const ws = new WeakSet();",
    complexity: { add: "O(1)", has: "O(1)", delete: "O(1)", iterate: "N/A" },
    note: "Not iterable, no size, no forEach. Values MUST be objects (or registered symbols).",
    methods: [
        { name: "add(obj)",       cat: "Insert",  desc: "Adds obj to the WeakSet — obj must be an object",                   code: `const ws = new WeakSet();\nconst obj = { id: 1 };\nws.add(obj);\n// ws now contains { id:1 }` },
        { name: "has(obj)",       cat: "Search",  desc: "Returns true if obj is in the WeakSet",                             code: `ws.has(obj); // true\nws.has({});  // false — different reference!` },
        { name: "delete(obj)",    cat: "Delete",  desc: "Removes obj; returns true if it was present",                       code: `ws.delete(obj); // true\nws.has(obj);   // false` },
        { name: "Visited guard",  cat: "Prototype", desc: "Classic pattern: guard against processing an object twice",       code: `const visited = new WeakSet();\n\nfunction process(node) {\n  if (visited.has(node)) return; // skip if seen\n  visited.add(node);\n  // ... do work with node\n}` },
        { name: "Circular ref",   cat: "Prototype", desc: "Detect circular references without preventing GC",                code: `function deepClone(obj, seen = new WeakSet()) {\n  if (seen.has(obj)) return '[Circular]';\n  seen.add(obj);\n\n  const clone = {};\n  for (const [k, v] of Object.entries(obj)) {\n    clone[k] = typeof v === 'object'\n      ? deepClone(v, seen) : v;\n  }\n  return clone;\n}` },
    ],
    questions: [
        {
            title: "Detect circular references in an object", difficulty: "Medium",
            solutions: [
                {
                    label: "🔵 WeakSet visited tracker",
                    tip: "WeakSet remembers which objects we've entered — if we see it again, it's circular.",
                    code: `function hasCircularRef(obj, seen = new WeakSet()) {
  // Base case: not an object
  if (typeof obj !== 'object' || obj === null) return false;

  // We've visited this object before → circular!
  if (seen.has(obj)) return true;

  // Mark as visited
  seen.add(obj);

  // Recursively check all values
  for (const val of Object.values(obj)) {
    if (hasCircularRef(val, seen)) return true;
  }
  return false;
}

const a = { x: 1 };
const b = { a };
a.b = b; // creates cycle: a.b.a.b.a...

console.log(hasCircularRef(a)); // true
console.log(hasCircularRef({ x: 1, y: { z: 2 } })); // false`,
                },
                {
                    label: "🟠 JSON.stringify try/catch",
                    tip: "Quick-and-dirty — JSON.stringify throws on circular refs.",
                    code: `function hasCircularRef(obj) {
  try {
    JSON.stringify(obj);
    return false; // serialised successfully → no cycle
  } catch (e) {
    // TypeError: Converting circular structure to JSON
    return true;
  }
}

const obj = {};
obj.self = obj; // circular!

console.log(hasCircularRef(obj)); // true`,
                },
                {
                    label: "🟢 Safe JSON stringify",
                    tip: "Custom replacer using WeakSet — serialises circular refs as '[Circular]'.",
                    code: `function safeStringify(obj) {
  const seen = new WeakSet();

  return JSON.stringify(obj, (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) return '[Circular]';
      seen.add(value);
    }
    return value;
  });
}

const obj = { a: 1 };
obj.self = obj;

console.log(safeStringify(obj));
// '{"a":1,"self":"[Circular]"}'`,
                },
            ],
        },
        {
            title: "Guard against double-processing objects", difficulty: "Easy",
            solutions: [
                {
                    label: "🔵 WeakSet processed guard",
                    tip: "WeakSet membership check prevents running side-effects twice.",
                    code: `const processed = new WeakSet();

function processTask(task) {
  // Already handled? Skip it
  if (processed.has(task)) {
    console.log('Already processed:', task.id);
    return;
  }

  // Do the work
  console.log('Processing:', task.id);
  task.status = 'done';

  // Mark as processed
  processed.add(task);
}

const task = { id: 1, status: 'pending' };
processTask(task); // Processing: 1
processTask(task); // Already processed: 1`,
                },
                {
                    label: "🟠 Property flag (comparison)",
                    tip: "Simpler but mutates the object — WeakSet is cleaner.",
                    code: `function processTask(task) {
  if (task._processed) {
    console.log('Already processed');
    return;
  }

  console.log('Processing:', task.id);
  task._processed = true; // ← mutates the object!

  // ⚠️ Downsides vs WeakSet:
  //   • Pollutes the object with _processed
  //   • Visible to all code that reads the object
  //   • Held in memory as long as the object lives
}`,
                },
                {
                    label: "🟢 Set with id (when objects have ids)",
                    tip: "If objects have unique ids, a plain Set of ids works fine.",
                    code: `const processedIds = new Set();

function processTask(task) {
  if (processedIds.has(task.id)) {
    console.log('Already processed:', task.id);
    return;
  }

  console.log('Processing:', task.id);
  processedIds.add(task.id);
}
// ✅ Use when: objects have stable unique IDs
// ✅ Use WeakSet when: no IDs, or objects are temporary`,
                },
            ],
        },
        {
            title: "Implement a seen-set for graph traversal", difficulty: "Medium",
            solutions: [
                {
                    label: "🔵 WeakSet for object nodes",
                    tip: "Object nodes in a graph are perfect WeakSet candidates.",
                    code: `function bfsGraph(startNode) {
  const visited = new WeakSet();
  const queue   = [startNode];
  const result  = [];

  while (queue.length) {
    const node = queue.shift();

    if (visited.has(node)) continue; // skip visited nodes
    visited.add(node);
    result.push(node.value);

    // Add unvisited neighbours to queue
    for (const neighbour of (node.neighbours || [])) {
      if (!visited.has(neighbour)) {
        queue.push(neighbour);
      }
    }
  }
  return result;
}`,
                },
                {
                    label: "🟠 Set of node IDs",
                    tip: "When nodes have unique string IDs, a plain Set of IDs is simpler.",
                    code: `function bfsGraph(startNode) {
  const visited = new Set();
  const queue   = [startNode];
  const result  = [];

  while (queue.length) {
    const node = queue.shift();
    if (visited.has(node.id)) continue;

    visited.add(node.id);
    result.push(node.value);

    for (const neighbour of (node.neighbours || [])) {
      queue.push(neighbour);
    }
  }
  return result;
}`,
                },
                {
                    label: "🟢 DFS with recursion",
                    tip: "Depth-first with WeakSet — elegant recursive approach.",
                    code: `function dfsGraph(node, visited = new WeakSet()) {
  if (!node || visited.has(node)) return [];
  visited.add(node);

  const result = [node.value];

  for (const neighbour of (node.neighbours || [])) {
    result.push(...dfsGraph(neighbour, visited));
  }
  return result;
}

// Example tree: A → B,C;  B → D
const D = { value:'D', neighbours:[] };
const B = { value:'B', neighbours:[D] };
const C = { value:'C', neighbours:[] };
const A = { value:'A', neighbours:[B,C] };
console.log(dfsGraph(A)); // ['A','B','D','C']`,
                },
            ],
        },
    ],
};

// ─────────────────────────────────────────────────────────────
//  DATA: Object
// ─────────────────────────────────────────────────────────────
const OBJECT_DATA = {
    icon: "📦", color: "#fbbf24",
    tagline: "Key→Value pairs — string/symbol keys, prototype chain",
    badge: "Object (Built-in, pre-ES6)",
    whenToUse: "Config objects, JSON data, record structs; string keys only",
    declare: "const obj = {};  // or  const obj = Object.create(null);",
    complexity: { get: "O(1)*", set: "O(1)*", has: "O(1)*", delete: "O(1)*" },
    note: "* Average O(1) with engine optimisation; use Map when keys are dynamic or non-string",
    methods: [
        { name: "obj.key / obj['key']",    cat: "Access",  desc: "Get a value — dot notation or bracket for dynamic keys",          code: `const obj = { name:'Alice', age:30 };\nobj.name;        // 'Alice'\nobj['age'];      // 30\nconst k = 'name';\nobj[k];          // 'Alice' ← dynamic key` },
        { name: "obj.key = val",           cat: "Modify",  desc: "Set or add a property — mutates the object",                     code: `const obj = { a: 1 };\nobj.a = 99;      // update\nobj.b = 'new';   // add new property` },
        { name: "delete obj.key",          cat: "Delete",  desc: "Removes a property — returns true (even if key didn't exist)",   code: `const obj = { a:1, b:2 };\ndelete obj.a;\nconsole.log(obj); // { b:2 }` },
        { name: "in operator",             cat: "Search",  desc: "True if key exists (own OR inherited via prototype)",             code: `const obj = { a:1 };\n'a' in obj;          // true\n'toString' in obj;  // true (inherited!)\n// Use hasOwn for own-only check:` },
        { name: "Object.hasOwn(obj, key)", cat: "Search",  desc: "True only for OWN properties — ignores prototype chain",         code: `const obj = { a:1 };\nObject.hasOwn(obj,'a');        // true\nObject.hasOwn(obj,'toString'); // false\n// Preferred over obj.hasOwnProperty()` },
        { name: "Object.keys(obj)",        cat: "Iterate", desc: "Returns array of OWN enumerable string keys",                    code: `const obj = { a:1, b:2, c:3 };\nObject.keys(obj);    // ['a','b','c']\n// Does NOT include prototype properties` },
        { name: "Object.values(obj)",      cat: "Iterate", desc: "Returns array of OWN enumerable values",                         code: `Object.values({ a:1, b:2 }); // [1,2]` },
        { name: "Object.entries(obj)",     cat: "Iterate", desc: "Returns array of [key, value] pairs — great for iteration",      code: `for (const [k,v] of Object.entries({a:1,b:2})) {\n  console.log(k, v);\n}` },
        { name: "Object.assign(t, ...src)",cat: "Modify",  desc: "Copies OWN enumerable props from sources to target — shallow",   code: `const base   = { a:1, b:2 };\nconst extras = { b:99, c:3 };\n// Mutates and returns target\nObject.assign(base, extras);\n// { a:1, b:99, c:3 }` },
        { name: "Spread { ...obj }",       cat: "Convert", desc: "Shallow clone or merge — cleaner than Object.assign",            code: `const copy   = { ...obj };\nconst merged = { ...obj1, ...obj2 };\n// Later keys overwrite earlier ones` },
        { name: "Object.freeze(obj)",      cat: "Modify",  desc: "Makes obj immutable — all attempts to modify silently fail",     code: `const config = Object.freeze({ MAX:100 });\nconfig.MAX = 999; // silently ignored!\nconsole.log(config.MAX); // still 100` },
        { name: "Object.fromEntries(arr)", cat: "Convert", desc: "Turns [key,value] pairs (e.g. from Map) into an Object",         code: `Object.fromEntries([['a',1],['b',2]]);\n// { a:1, b:2 }\n// Also works on Map:\nObject.fromEntries(new Map([['x',10]]));` },
        { name: "Destructuring { k }",     cat: "Access",  desc: "Extract and rename properties inline — very common in JS",       code: `const { name, age, city='Unknown' } = user;\n// city defaults to 'Unknown' if missing\nconst { name: firstName } = user; // rename` },
        { name: "Optional chaining ?.",    cat: "Access",  desc: "Safe property access — returns undefined instead of throwing",   code: `const user = { address: null };\nuser?.address?.city;       // undefined (no throw!)\nuser?.getAge?.();          // undefined if no method` },
        { name: "Nullish coalescing ??",   cat: "Access",  desc: "Returns right side only when left is null or undefined",         code: `const name = user.name ?? 'Anonymous';\n// ⚠️ Different from || which also catches 0,'':\n0  || 'default'  // 'default' (wrong for 0!)\n0  ?? 'default'  // 0 ✅` },
    ],
    questions: [
        {
            title: "Deep clone an object", difficulty: "Medium",
            solutions: [
                {
                    label: "🔵 structuredClone (ES2022)",
                    tip: "Built-in deep clone — handles nested objects, arrays, dates, and circular refs!",
                    code: `const original = {
  name: 'Alice',
  address: { city: 'NYC', zip: 10001 },
  scores: [95, 87, 92],
  born: new Date('1990-01-15')
};

// structuredClone is the modern standard
const clone = structuredClone(original);

clone.address.city = 'LA'; // doesn't affect original!
console.log(original.address.city); // 'NYC' ✅

// ⚠️ Cannot clone: functions, DOM nodes, class instances`,
                },
                {
                    label: "🟠 JSON round-trip",
                    tip: "Simple but loses functions, undefined, Dates, and circular refs.",
                    code: `const original = { a: 1, b: { c: [1, 2, 3] } };

// Serialise to JSON string then parse back
const clone = JSON.parse(JSON.stringify(original));

clone.b.c.push(4);
console.log(original.b.c); // [1,2,3] ✅ not affected

// ⚠️ Limitations:
// Date → string (loses Date type)
// undefined, functions → stripped!
// NaN, Infinity → null
// Circular refs → throws TypeError`,
                },
                {
                    label: "🟢 Recursive deep clone",
                    tip: "Manual implementation — handles arrays and objects, good learning exercise.",
                    code: `function deepClone(value) {
  // Base case: primitives are already immutable
  if (typeof value !== 'object' || value === null) {
    return value;
  }

  // Handle Arrays
  if (Array.isArray(value)) {
    return value.map(deepClone);
  }

  // Handle plain Objects
  const clone = {};
  for (const [key, val] of Object.entries(value)) {
    clone[key] = deepClone(val); // recurse!
  }
  return clone;
}

const obj = { a: 1, b: { c: [1, 2] } };
const copy = deepClone(obj);
copy.b.c.push(3);
console.log(obj.b.c); // [1,2] — original safe ✅`,
                },
            ],
        },
        {
            title: "Flatten a nested object (dot notation keys)", difficulty: "Medium",
            solutions: [
                {
                    label: "🔵 Recursive Object.entries",
                    tip: "Build flat key by joining parent + child keys with a dot.",
                    code: `function flattenObject(obj, prefix = '') {
  return Object.entries(obj).reduce((flat, [key, val]) => {
    const fullKey = prefix ? \`\${prefix}.\${key}\` : key;

    if (typeof val === 'object' && val !== null &&
        !Array.isArray(val)) {
      // Recurse into nested object, passing fullKey as prefix
      Object.assign(flat, flattenObject(val, fullKey));
    } else {
      flat[fullKey] = val;
    }
    return flat;
  }, {});
}

flattenObject({ a: { b: { c: 1 }, d: 2 }, e: 3 });
// { 'a.b.c':1, 'a.d':2, 'e':3 }`,
                },
                {
                    label: "🟠 Stack-based iterative",
                    tip: "Avoids call-stack overflow on deeply nested objects.",
                    code: `function flattenObject(obj) {
  const result = {};
  const stack  = [{ obj, prefix: '' }];

  while (stack.length) {
    const { obj: current, prefix } = stack.pop();

    for (const [key, val] of Object.entries(current)) {
      const fullKey = prefix ? \`\${prefix}.\${key}\` : key;

      if (typeof val === 'object' && val !== null
          && !Array.isArray(val)) {
        stack.push({ obj: val, prefix: fullKey });
      } else {
        result[fullKey] = val;
      }
    }
  }
  return result;
}`,
                },
                {
                    label: "🟢 Unflatten (reverse operation)",
                    tip: "The inverse — convert dot-notation keys back to nested object.",
                    code: `function unflattenObject(flat) {
  const result = {};

  for (const [dotKey, value] of Object.entries(flat)) {
    const keys = dotKey.split('.');
    let current = result;

    // Walk all keys except last — create nested objects
    for (let i = 0; i < keys.length - 1; i++) {
      current[keys[i]] ??= {}; // create if missing
      current = current[keys[i]];
    }
    // Set the final value at the deepest level
    current[keys.at(-1)] = value;
  }
  return result;
}

unflattenObject({ 'a.b.c':1, 'a.d':2, 'e':3 });
// { a: { b: { c:1 }, d:2 }, e:3 }`,
                },
            ],
        },
        {
            title: "Merge two objects (deep merge)", difficulty: "Hard",
            solutions: [
                {
                    label: "🔵 Recursive deep merge",
                    tip: "Recursively merge nested objects — spread only does shallow copy.",
                    code: `function deepMerge(target, source) {
  // Start with a shallow copy of target
  const result = { ...target };

  for (const [key, srcVal] of Object.entries(source)) {
    const tgtVal = result[key];

    if (
      typeof srcVal === 'object' && srcVal !== null &&
      typeof tgtVal === 'object' && tgtVal !== null &&
      !Array.isArray(srcVal)
    ) {
      // Both are objects → recurse to merge deeply
      result[key] = deepMerge(tgtVal, srcVal);
    } else {
      // Primitive or one side is missing → source wins
      result[key] = srcVal;
    }
  }
  return result;
}

const a = { x:1, n:{ a:1, b:2 } };
const b = { y:2, n:{ b:99, c:3 } };
deepMerge(a, b);
// { x:1, y:2, n:{ a:1, b:99, c:3 } }`,
                },
                {
                    label: "🟠 Spread (shallow — common mistake!)",
                    tip: "Spread only merges one level deep — nested objects are overwritten, not merged!",
                    code: `const a = { x:1, nested:{ a:1, b:2 } };
const b = { y:2, nested:{ c:3 } };

// ⚠️ SHALLOW MERGE — nested is REPLACED not merged
const shallow = { ...a, ...b };
// { x:1, y:2, nested:{ c:3 } }
//                     ↑ a and b are LOST!

// ✅ This is fine for flat objects:
const flat = { ...defaults, ...overrides };`,
                },
                {
                    label: "🟢 structuredClone + entries loop",
                    tip: "Clone first to avoid mutating input, then merge.",
                    code: `function safeMerge(target, source) {
  // Deep clone target so we never mutate the input
  const result = structuredClone(target);

  for (const [k, v] of Object.entries(source)) {
    if (
      typeof v === 'object' && v !== null
      && k in result && typeof result[k] === 'object'
    ) {
      result[k] = safeMerge(result[k], v);
    } else {
      result[k] = structuredClone(v); // deep copy source value
    }
  }
  return result;
}`,
                },
            ],
        },
    ],
};

// ─────────────────────────────────────────────────────────────
//  DATA: LinkedList (custom implementation)
// ─────────────────────────────────────────────────────────────
const LINKED_LIST_DATA = {
    icon: "🔗", color: "#e879f9",
    tagline: "Custom doubly-linked list — O(1) head/tail insert, O(n) access",
    badge: "LinkedList (Custom Class)",
    whenToUse: "Frequent head/tail operations; implementing Deque/Queue with O(1) ends",
    declare: "const ll = new LinkedList();",
    complexity: { prepend: "O(1)", append: "O(1)", remove: "O(n)", search: "O(n)" },
    note: "JS has no built-in LinkedList. This is the standard interview implementation.",
    methods: [
        { name: "Node class",             cat: "Prototype", desc: "Building block: each node holds a value + prev/next pointers",  code: `class Node {\n  constructor(value) {\n    this.value = value;\n    this.next  = null; // pointer to next node\n    this.prev  = null; // pointer to previous node\n  }\n}` },
        { name: "LinkedList class",       cat: "Prototype", desc: "Holds head, tail, and size for O(1) end access",                code: `class LinkedList {\n  constructor() {\n    this.head = null; // first node\n    this.tail = null; // last node\n    this.size = 0;\n  }\n}` },
        { name: "append(value)",          cat: "Insert",  desc: "Add to end (tail) — always O(1) because we track tail",           code: `append(value) {\n  const node = new Node(value);\n  if (!this.tail) {\n    this.head = this.tail = node; // first element\n  } else {\n    node.prev  = this.tail;\n    this.tail.next = node;\n    this.tail      = node;\n  }\n  this.size++;\n}` },
        { name: "prepend(value)",         cat: "Insert",  desc: "Add to front (head) — always O(1) because we track head",         code: `prepend(value) {\n  const node = new Node(value);\n  if (!this.head) {\n    this.head = this.tail = node;\n  } else {\n    node.next       = this.head;\n    this.head.prev  = node;\n    this.head       = node;\n  }\n  this.size++;\n}` },
        { name: "removeHead()",           cat: "Delete",  desc: "Remove and return the first element — O(1)",                      code: `removeHead() {\n  if (!this.head) return null;\n  const val = this.head.value;\n  this.head = this.head.next;\n  if (this.head) this.head.prev = null;\n  else this.tail = null; // list is now empty\n  this.size--;\n  return val;\n}` },
        { name: "removeTail()",           cat: "Delete",  desc: "Remove and return the last element — O(1)",                       code: `removeTail() {\n  if (!this.tail) return null;\n  const val = this.tail.value;\n  this.tail = this.tail.prev;\n  if (this.tail) this.tail.next = null;\n  else this.head = null;\n  this.size--;\n  return val;\n}` },
        { name: "find(value)",            cat: "Search",  desc: "Linear scan from head — O(n)",                                    code: `find(value) {\n  let curr = this.head;\n  while (curr) {\n    if (curr.value === value) return curr;\n    curr = curr.next;\n  }\n  return null;\n}` },
        { name: "toArray()",              cat: "Convert", desc: "Collect all values into a plain JS Array",                        code: `toArray() {\n  const result = [];\n  let curr = this.head;\n  while (curr) {\n    result.push(curr.value);\n    curr = curr.next;\n  }\n  return result;\n}` },
        { name: "[Symbol.iterator]()",    cat: "Iterate", desc: "Makes LinkedList usable in for...of loops",                       code: `*[Symbol.iterator]() {\n  let curr = this.head;\n  while (curr) {\n    yield curr.value;\n    curr = curr.next;\n  }\n}\n// Now: for (const val of myList) { ... }` },
    ],
    questions: [
        {
            title: "Reverse a Linked List", difficulty: "Medium",
            solutions: [
                {
                    label: "🔵 Iterative pointer swap",
                    tip: "Walk through flipping each node's next pointer. O(n) time, O(1) space.",
                    code: `function reverseList(head) {
  let prev = null;
  let curr = head;

  while (curr !== null) {
    const next = curr.next; // save next before we lose it

    curr.next = prev;  // flip pointer backward
    prev = curr;       // advance prev to current
    curr = next;       // advance current to saved next
  }
  // prev is now the new head (last original node)
  return prev;
}

// Usage:
// 1 → 2 → 3 → null  becomes  3 → 2 → 1 → null`,
                },
                {
                    label: "🟠 Recursive",
                    tip: "Elegant recursion. Base case: empty list or single node.",
                    code: `function reverseList(head) {
  // Base case: empty or single-node list
  if (!head || !head.next) return head;

  // Reverse the rest of the list
  const newHead = reverseList(head.next);

  // Make the next node point back to current
  head.next.next = head;
  head.next = null; // current becomes the new tail

  return newHead;
}
// ⚠️ O(n) call stack depth — use iterative for very long lists`,
                },
                {
                    label: "🟢 Array helper",
                    tip: "Extract values, reverse array, rebuild list — clearest to read.",
                    code: `function reverseList(head) {
  // Collect all values into an array
  const values = [];
  let curr = head;
  while (curr) { values.push(curr.value); curr = curr.next; }

  // Rebuild the list in reverse
  let newHead = null, tail = null;
  for (const val of values.reverse()) {
    const node = new Node(val);
    if (!newHead) { newHead = tail = node; }
    else { tail.next = node; tail = node; }
  }
  return newHead;
}`,
                },
            ],
        },
        {
            title: "Detect a cycle in a Linked List", difficulty: "Medium",
            solutions: [
                {
                    label: "🔵 Floyd's cycle detection",
                    tip: "Slow pointer moves 1 step, fast moves 2. If they meet, there's a cycle.",
                    code: `function hasCycle(head) {
  let slow = head;
  let fast = head;

  while (fast !== null && fast.next !== null) {
    slow = slow.next;       // 1 step
    fast = fast.next.next;  // 2 steps

    // If they meet, there's a loop
    if (slow === fast) return true;
  }

  // fast reached the end → no cycle
  return false;
}
// O(n) time, O(1) space — the best approach`,
                },
                {
                    label: "🟠 WeakSet of visited nodes",
                    tip: "Mark visited nodes — if we see a node twice, it's a cycle.",
                    code: `function hasCycle(head) {
  // WeakSet holds object references without preventing GC
  const visited = new WeakSet();

  let curr = head;
  while (curr !== null) {
    if (visited.has(curr)) return true; // seen before → cycle!
    visited.add(curr);
    curr = curr.next;
  }
  return false;
}
// O(n) time, O(n) space — simpler but uses more memory`,
                },
                {
                    label: "🟢 Find cycle start node",
                    tip: "Extend Floyd's: after meeting, reset one pointer to head. They meet at cycle start.",
                    code: `function detectCycleStart(head) {
  let slow = head, fast = head;
  let hasCycle = false;

  // Phase 1: detect cycle
  while (fast && fast.next) {
    slow = slow.next;
    fast = fast.next.next;
    if (slow === fast) { hasCycle = true; break; }
  }

  if (!hasCycle) return null;

  // Phase 2: find where cycle STARTS
  slow = head; // reset slow to head
  while (slow !== fast) {
    slow = slow.next;
    fast = fast.next; // both move 1 step now
  }
  // They meet exactly at the cycle entry node
  return slow;
}`,
                },
            ],
        },
        {
            title: "Implement a Queue using LinkedList", difficulty: "Easy",
            solutions: [
                {
                    label: "🔵 Full Queue class",
                    tip: "enqueue at tail, dequeue from head — both O(1) with our doubly-linked list.",
                    code: `class Queue {
  constructor() {
    this.list = new LinkedList();
  }

  // Add to back of queue — O(1)
  enqueue(value) {
    this.list.append(value);
  }

  // Remove from front — O(1)
  dequeue() {
    return this.list.removeHead();
  }

  // Peek without removing — O(1)
  peek() {
    return this.list.head?.value ?? null;
  }

  get size() { return this.list.size; }
  isEmpty()  { return this.list.size === 0; }
}

const q = new Queue();
q.enqueue('A'); q.enqueue('B'); q.enqueue('C');
q.dequeue(); // 'A'
q.peek();    // 'B'`,
                },
                {
                    label: "🟠 Simple Array queue",
                    tip: "Array works but shift() is O(n). Fine for small queues.",
                    code: `class Queue {
  constructor() { this.items = []; }

  enqueue(value) { this.items.push(value); }

  // ⚠️ shift() is O(n) — re-indexes all elements
  dequeue()      { return this.items.shift(); }

  peek()         { return this.items[0]; }
  get size()     { return this.items.length; }
  isEmpty()      { return this.items.length === 0; }
}
// Use LinkedList version for performance-critical code`,
                },
                {
                    label: "🟢 Two-stack queue",
                    tip: "Classic interview trick: two stacks simulate O(1) amortised queue.",
                    code: `class Queue {
  constructor() {
    this.inbox  = []; // push here
    this.outbox = []; // pop from here
  }

  enqueue(value) {
    this.inbox.push(value);
  }

  dequeue() {
    // When outbox is empty, pour inbox into it
    if (!this.outbox.length) {
      while (this.inbox.length) {
        this.outbox.push(this.inbox.pop());
      }
    }
    return this.outbox.pop();
  }
}
// Each element is moved at most once → O(1) amortised`,
                },
            ],
        },
    ],
};

// ─────────────────────────────────────────────────────────────
//  DATA: Stack (custom implementation)
// ─────────────────────────────────────────────────────────────
const STACK_DATA = {
    icon: "🥞", color: "#34d399",
    tagline: "LIFO — Last In, First Out. push/pop from the same end",
    badge: "Stack (Custom Class)",
    whenToUse: "Undo/redo, call stack simulation, DFS, balanced brackets, expression parsing",
    declare: "const stack = new Stack();",
    complexity: { push: "O(1)", pop: "O(1)", peek: "O(1)", search: "O(n)" },
    note: "JS arrays work as a stack natively via push/pop — the class just adds semantics",
    methods: [
        { name: "Stack class",         cat: "Prototype", desc: "Stack backed by a JS array for simplicity and performance",     code: `class Stack {\n  constructor() {\n    this.items = []; // array as the underlying store\n  }\n\n  get size()  { return this.items.length; }\n  isEmpty()   { return this.items.length === 0; }\n}` },
        { name: "push(value)",         cat: "Insert",  desc: "Adds value to the TOP of the stack — O(1)",                      code: `push(value) {\n  this.items.push(value);\n  return this; // allow chaining\n}\n// stack.push(1).push(2).push(3)` },
        { name: "pop()",               cat: "Delete",  desc: "Removes and returns the TOP element — O(1)",                     code: `pop() {\n  if (this.isEmpty()) return null;\n  return this.items.pop();\n}` },
        { name: "peek()",              cat: "Access",  desc: "Returns the TOP element WITHOUT removing it — O(1)",             code: `peek() {\n  if (this.isEmpty()) return null;\n  return this.items.at(-1); // last element\n}` },
        { name: "clear()",             cat: "Delete",  desc: "Empties the entire stack",                                        code: `clear() {\n  this.items = [];\n}` },
        { name: "toArray()",           cat: "Convert", desc: "Returns copy of items — bottom to top order",                    code: `toArray() {\n  return [...this.items]; // shallow copy\n}` },
        { name: "contains(value)",     cat: "Search",  desc: "Linear scan — O(n). Stacks aren't designed for search",          code: `contains(value) {\n  return this.items.includes(value);\n}` },
        { name: "Native array stack",  cat: "Prototype", desc: "JS arrays are already stacks — just use push/pop directly",   code: `// You don't NEED a Stack class in JS:\nconst stack = [];\nstack.push(1); // [1]\nstack.push(2); // [1,2]\nstack.pop();   // 2 — [1]\nstack.at(-1);  // peek → 1` },
    ],
    questions: [
        {
            title: "Validate balanced brackets", difficulty: "Easy",
            solutions: [
                {
                    label: "🔵 Stack with Map lookup",
                    tip: "Push open brackets, pop and verify when we see a closing bracket.",
                    code: `function isBalanced(str) {
  const stack = [];
  // Map: closing bracket → expected opening bracket
  const pairs = new Map([
    [')', '('],
    [']', '['],
    ['}', '{'],
  ]);

  for (const char of str) {
    if ('([{'.includes(char)) {
      stack.push(char); // push all opening brackets
    } else if (pairs.has(char)) {
      // Closing bracket: check it matches top of stack
      if (stack.pop() !== pairs.get(char)) return false;
    }
  }

  // Stack must be empty — all openers were closed
  return stack.length === 0;
}

isBalanced('({[]})');  // true
isBalanced('({[})');   // false`,
                },
                {
                    label: "🟠 Replace string approach",
                    tip: "Repeatedly remove valid pairs until nothing changes — O(n²) but intuitive.",
                    code: `function isBalanced(str) {
  // Keep removing matching pairs until no more found
  let prev = '';
  while (str !== prev) {
    prev = str;
    str = str
      .replace('()', '')
      .replace('[]', '')
      .replace('{}', '');
  }
  // If all brackets matched, string is empty
  return str.length === 0;
}
// isBalanced('({[]})') → true
// ⚠️ Avoid in production — O(n²) worst case`,
                },
                {
                    label: "🟢 Counter for single bracket type",
                    tip: "When only one bracket type: simple counter is enough.",
                    code: `function isBalancedParens(str) {
  let count = 0;

  for (const char of str) {
    if (char === '(') count++;
    if (char === ')') count--;
    // If count goes negative, ')' appeared before '('
    if (count < 0) return false;
  }
  return count === 0;
}
// isBalancedParens('(())') → true
// isBalancedParens('())(' ) → false`,
                },
            ],
        },
        {
            title: "Evaluate a Reverse Polish Notation expression", difficulty: "Medium",
            solutions: [
                {
                    label: "🔵 Stack-based RPN evaluator",
                    tip: "Push numbers; on operator, pop two operands, compute, push result.",
                    code: `function evalRPN(tokens) {
  const stack = [];
  const operators = new Set(['+', '-', '*', '/']);

  for (const token of tokens) {
    if (!operators.has(token)) {
      // It's a number — push onto stack
      stack.push(Number(token));
    } else {
      // Pop the two most recent operands
      const b = stack.pop(); // right operand
      const a = stack.pop(); // left operand

      switch (token) {
        case '+': stack.push(a + b); break;
        case '-': stack.push(a - b); break;
        case '*': stack.push(a * b); break;
        case '/': stack.push(Math.trunc(a / b)); break;
      }
    }
  }
  // The final remaining value is the result
  return stack.pop();
}

evalRPN(['2','1','+','3','*']); // (2+1)*3 = 9`,
                },
                {
                    label: "🟠 With error handling",
                    tip: "Production-ready version with divide-by-zero and invalid token guards.",
                    code: `function evalRPN(tokens) {
  const stack = [];

  for (const token of tokens) {
    const num = Number(token);

    if (!isNaN(num)) {
      stack.push(num);
      continue;
    }

    if (stack.length < 2) throw new Error('Invalid RPN');

    const b = stack.pop();
    const a = stack.pop();

    if (token === '/' && b === 0) throw new Error('Divide by zero');

    const ops = {'+':a+b, '-':a-b, '*':a*b, '/':Math.trunc(a/b)};
    if (!(token in ops)) throw new Error(\`Unknown op: \${token}\`);

    stack.push(ops[token]);
  }

  if (stack.length !== 1) throw new Error('Invalid expression');
  return stack[0];
}`,
                },
                {
                    label: "🟢 Min-stack (track minimum)",
                    tip: "Extend stack to return min in O(1) — classic interview problem.",
                    code: `class MinStack {
  constructor() {
    this.stack    = [];
    this.minStack = []; // parallel stack tracking minimums
  }

  push(val) {
    this.stack.push(val);
    // Push new min: current val vs previous min
    const currentMin = this.minStack.length
      ? Math.min(val, this.minStack.at(-1))
      : val;
    this.minStack.push(currentMin);
  }

  pop() {
    this.minStack.pop(); // keep in sync
    return this.stack.pop();
  }

  getMin() {
    return this.minStack.at(-1); // O(1)!
  }
}`,
                },
            ],
        },
        {
            title: "Implement DFS using a Stack", difficulty: "Medium",
            solutions: [
                {
                    label: "🔵 Iterative DFS with Stack",
                    tip: "Replace the recursive call stack with an explicit stack — avoids stack overflow.",
                    code: `function dfs(graph, start) {
  const visited = new Set();
  const stack   = [start];
  const result  = [];

  while (stack.length) {
    const node = stack.pop(); // take from top (LIFO)

    if (visited.has(node)) continue;
    visited.add(node);
    result.push(node);

    // Push neighbours in reverse so left-to-right order
    const neighbours = graph[node] || [];
    for (let i = neighbours.length - 1; i >= 0; i--) {
      if (!visited.has(neighbours[i])) {
        stack.push(neighbours[i]);
      }
    }
  }
  return result;
}

const graph = { A:['B','C'], B:['D'], C:['D'], D:[] };
dfs(graph, 'A'); // ['A','B','D','C']`,
                },
                {
                    label: "🟠 Recursive DFS",
                    tip: "Cleaner code — JS engine's own call stack acts as the stack.",
                    code: `function dfs(graph, node, visited = new Set()) {
  if (visited.has(node)) return [];
  visited.add(node);

  const result = [node];
  for (const neighbour of (graph[node] || [])) {
    result.push(...dfs(graph, neighbour, visited));
  }
  return result;
}

const graph = { A:['B','C'], B:['D'], C:['D'], D:[] };
dfs(graph, 'A'); // ['A','B','D','C']`,
                },
                {
                    label: "🟢 BFS using Queue (comparison)",
                    tip: "BFS uses a queue (FIFO) instead of stack — useful contrast.",
                    code: `function bfs(graph, start) {
  const visited = new Set([start]);
  const queue   = [start]; // FIFO not LIFO
  const result  = [];

  while (queue.length) {
    const node = queue.shift(); // take from FRONT (FIFO)
    result.push(node);

    for (const neighbour of (graph[node] || [])) {
      if (!visited.has(neighbour)) {
        visited.add(neighbour);
        queue.push(neighbour); // add to BACK
      }
    }
  }
  return result;
}
// BFS: shortest path level by level
// DFS: exhausts one branch before backtracking`,
                },
            ],
        },
    ],
};

// ─────────────────────────────────────────────────────────────
//  DATA: Algorithms
// ─────────────────────────────────────────────────────────────
const ALGORITHMS_DATA = {
    icon: "⚡", color: "#60a5fa",
    tagline: "Core algorithm patterns — the foundation of every interview",
    badge: "Algorithms (ES6+)",
    whenToUse: "Choose the pattern that matches the problem's structure",
    declare: "// Pattern-based — no single declaration",
    complexity: { search: "O(log n)", sort: "O(n log n)", scan: "O(n)", recurse: "O(2ⁿ)*" },
    note: "* Recursion without memoization is exponential. Always cache repeated subproblems.",
    methods: [], // Algorithms section has no methods — only practice problems
    questions: [
        {
            title: "Binary Search — find target in sorted array", difficulty: "Easy",
            solutions: [
                {
                    label: "🔵 Classic iterative",
                    tip: "Use Math.floor((l+r)/2) or (l + r) >> 1. Always verify sorted input first.",
                    code: `function binarySearch(arr, target) {
  let left  = 0;
  let right = arr.length - 1;

  while (left <= right) {
    // Bit shift: same as Math.floor((left+right)/2) but faster
    const mid = (left + right) >> 1;

    if (arr[mid] === target)      return mid;
    else if (arr[mid] < target)  left  = mid + 1;
    else                         right = mid - 1;
  }
  return -1; // not found
}

binarySearch([2,5,8,12,16,23,38], 23); // 5
binarySearch([2,5,8,12,16,23,38], 10); // -1`,
                },
                {
                    label: "🟠 Recursive",
                    tip: "Recursive version is cleaner but uses O(log n) call stack space.",
                    code: `function binarySearch(arr, target, left=0, right=arr.length-1) {
  // Base case: search space exhausted
  if (left > right) return -1;

  const mid = (left + right) >> 1;

  if (arr[mid] === target)     return mid;
  if (arr[mid] < target)
    return binarySearch(arr, target, mid + 1, right);

  return binarySearch(arr, target, left, mid - 1);
}`,
                },
                {
                    label: "🟢 Find insertion position",
                    tip: "Classic variant: where would target be inserted to keep array sorted?",
                    code: `function searchInsertPosition(arr, target) {
  let left = 0, right = arr.length - 1;

  while (left <= right) {
    const mid = (left + right) >> 1;
    if (arr[mid] === target) return mid;
    if (arr[mid] < target)  left  = mid + 1;
    else                    right = mid - 1;
  }
  // left is the correct insertion index
  return left;
}

searchInsertPosition([1,3,5,6], 5); // 2 (found at 2)
searchInsertPosition([1,3,5,6], 2); // 1 (insert before 3)
searchInsertPosition([1,3,5,6], 7); // 4 (append to end)`,
                },
            ],
        },
        {
            title: "Sorting — custom comparators", difficulty: "Easy",
            solutions: [
                {
                    label: "🔵 Array.sort() essentials",
                    tip: "Default sort converts to STRINGS — always provide a comparator for numbers!",
                    code: `// ⚠️ Default sort is lexicographic (treats as strings):
[10, 9, 2, 100].sort();        // [10, 100, 2, 9] WRONG!

// ✅ Numeric ascending:
[10, 9, 2, 100].sort((a, b) => a - b); // [2,9,10,100]

// ✅ Numeric descending:
[10, 9, 2, 100].sort((a, b) => b - a); // [100,10,9,2]

// ✅ Sort strings alphabetically:
['banana','apple','cherry'].sort();     // ['apple','banana','cherry']

// ✅ Sort objects by property:
users.sort((a, b) => a.age - b.age);`,
                },
                {
                    label: "🟠 Multi-criteria sort",
                    tip: "Chain criteria: sort by primary, then secondary for ties.",
                    code: `const students = [
  { name: 'Charlie', grade: 85 },
  { name: 'Alice',   grade: 92 },
  { name: 'Bob',     grade: 85 },
  { name: 'Dave',    grade: 92 },
];

// Sort by grade DESC, then name ASC for ties
students.sort((a, b) => {
  if (b.grade !== a.grade) return b.grade - a.grade;
  return a.name.localeCompare(b.name);
});
// [Alice 92, Dave 92, Bob 85, Charlie 85]`,
                },
                {
                    label: "🟢 Merge Sort (manual)",
                    tip: "Divide-and-conquer O(n log n) — stable, and good to understand deeply.",
                    code: `function mergeSort(arr) {
  if (arr.length <= 1) return arr; // base case

  const mid   = Math.floor(arr.length / 2);
  const left  = mergeSort(arr.slice(0, mid));
  const right = mergeSort(arr.slice(mid));

  return merge(left, right);
}

function merge(left, right) {
  const result = [];
  let i = 0, j = 0;

  while (i < left.length && j < right.length) {
    // Pick smaller of the two heads
    if (left[i] <= right[j]) result.push(left[i++]);
    else                     result.push(right[j++]);
  }
  // Append any remaining elements
  return [...result, ...left.slice(i), ...right.slice(j)];
}

mergeSort([5,2,8,1,9,3]); // [1,2,3,5,8,9]`,
                },
            ],
        },
        {
            title: "Two Pointers — find pair with target sum", difficulty: "Easy",
            solutions: [
                {
                    label: "🔵 Left + right converge",
                    tip: "Only works on SORTED arrays. O(n) — one pass, no extra space.",
                    code: `function twoSumSorted(arr, target) {
  let left  = 0;
  let right = arr.length - 1;

  while (left < right) {
    const sum = arr[left] + arr[right];

    if (sum === target)       return [left, right];
    else if (sum < target)   left++;  // need bigger sum
    else                     right--; // need smaller sum
  }
  return null;
}

twoSumSorted([1,3,5,7,9,11], 12); // [1,5] → 3+9`,
                },
                {
                    label: "🟠 Palindrome check",
                    tip: "Two pointers meet in the middle — O(n) with O(1) space.",
                    code: `function isPalindrome(str) {
  // Normalise: lowercase, letters/numbers only
  const clean = str.toLowerCase().replace(/[^a-z0-9]/g, '');

  let left  = 0;
  let right = clean.length - 1;

  while (left < right) {
    if (clean[left] !== clean[right]) return false;
    left++;
    right--;
  }
  return true;
}

isPalindrome('A man a plan a canal Panama'); // true
isPalindrome('race a car');                  // false`,
                },
                {
                    label: "🟢 Remove duplicates in-place",
                    tip: "Slow pointer writes; fast pointer reads — classic in-place technique.",
                    code: `function removeDuplicates(nums) {
  if (!nums.length) return 0;

  let slow = 0; // index where next unique value goes

  for (let fast = 1; fast < nums.length; fast++) {
    if (nums[fast] !== nums[slow]) {
      slow++;
      nums[slow] = nums[fast]; // write next unique
    }
  }
  // nums[0..slow] is the deduplicated portion
  return slow + 1;
}

const arr = [1,1,2,3,3,4];
const len = removeDuplicates(arr);
console.log(arr.slice(0, len)); // [1,2,3,4]`,
                },
            ],
        },
        {
            title: "Sliding Window — max sum subarray of size k", difficulty: "Medium",
            solutions: [
                {
                    label: "🔵 Fixed window",
                    tip: "Build first window then slide: add new right element, remove old left element.",
                    code: `function maxSumSubarray(arr, k) {
  // Build the first window
  let windowSum = 0;
  for (let i = 0; i < k; i++) windowSum += arr[i];

  let maxSum = windowSum;

  // Slide the window: add arr[i], remove arr[i-k]
  for (let i = k; i < arr.length; i++) {
    windowSum += arr[i] - arr[i - k];
    maxSum = Math.max(maxSum, windowSum);
  }
  return maxSum;
}

maxSumSubarray([2,1,5,1,3,2], 3); // 9 (5+1+3)`,
                },
                {
                    label: "🟠 Variable window — longest no-repeat substring",
                    tip: "Expand right; shrink left when constraint violated.",
                    code: `function longestUniqueSubstring(s) {
  const charSet = new Set();
  let left = 0, maxLen = 0;

  for (let right = 0; right < s.length; right++) {
    // Shrink window from left until duplicate is gone
    while (charSet.has(s[right])) {
      charSet.delete(s[left]);
      left++;
    }
    charSet.add(s[right]);
    maxLen = Math.max(maxLen, right - left + 1);
  }
  return maxLen;
}

longestUniqueSubstring('abcabcbb'); // 3 ("abc")
longestUniqueSubstring('pwwkew');   // 3 ("wke")`,
                },
                {
                    label: "🟢 Minimum window substring",
                    tip: "Find smallest window containing all required chars. Expand then contract.",
                    code: `function minWindowSubstring(s, t) {
  if (!t.length) return '';

  // Count required characters
  const need = new Map();
  for (const c of t) need.set(c, (need.get(c)||0)+1);

  let left=0, formed=0, required=need.size;
  const window = new Map();
  let ans = [Infinity, 0, 0]; // [length, l, r]

  for (let right=0; right<s.length; right++) {
    const c = s[right];
    window.set(c, (window.get(c)||0)+1);
    if (need.has(c) && window.get(c)===need.get(c)) formed++;

    while (formed === required) {
      if (right-left+1 < ans[0]) ans=[right-left+1,left,right];
      const l=s[left++];
      window.set(l, window.get(l)-1);
      if (need.has(l) && window.get(l)<need.get(l)) formed--;
    }
  }
  return ans[0]===Infinity ? '' : s.slice(ans[1],ans[2]+1);
}`,
                },
            ],
        },
        {
            title: "Map/Object pattern — subarray sum equals k", difficulty: "Medium",
            solutions: [
                {
                    label: "🔵 Prefix sum + Map",
                    tip: "Key insight: if prefixSum[j] - prefixSum[i] = k, then subarray [i+1..j] sums to k.",
                    code: `function subarraySum(nums, k) {
  // prefixCount: how many times each prefix sum has occurred
  const prefixCount = new Map([[0, 1]]); // empty prefix
  let sum = 0, count = 0;

  for (const num of nums) {
    sum += num;
    // If (sum - k) was a prefix sum before, we found a valid subarray
    count += prefixCount.get(sum - k) || 0;
    prefixCount.set(sum, (prefixCount.get(sum) || 0) + 1);
  }
  return count;
}

subarraySum([1,1,1], 2); // 2
subarraySum([1,2,3], 3); // 2`,
                },
                {
                    label: "🟠 Anagram check with char frequency",
                    tip: "Map of char counts — if two strings share the same map, they're anagrams.",
                    code: `function isAnagram(s, t) {
  if (s.length !== t.length) return false;

  const count = new Map();

  // Increment for chars in s, decrement for t
  for (const c of s) count.set(c, (count.get(c)||0)+1);
  for (const c of t) {
    count.set(c, (count.get(c)||0)-1);
    if (count.get(c) < 0) return false; // t has more c than s
  }
  return true;
}

isAnagram('anagram', 'nagaram'); // true
isAnagram('rat', 'car');         // false`,
                },
                {
                    label: "🟢 Longest consecutive sequence",
                    tip: "Use Set for O(1) lookup. Only start counting from the sequence's beginning.",
                    code: `function longestConsecutive(nums) {
  const numSet = new Set(nums);
  let longest  = 0;

  for (const num of numSet) {
    // Only start counting if num is the BEGINNING of a sequence
    if (!numSet.has(num - 1)) {
      let current  = num;
      let length   = 1;

      // Extend the sequence forward
      while (numSet.has(current + 1)) {
        current++;
        length++;
      }
      longest = Math.max(longest, length);
    }
  }
  return longest;
}

longestConsecutive([100,4,200,1,3,2]); // 4 → [1,2,3,4]`,
                },
            ],
        },
        {
            title: "Recursion & Backtracking — generate permutations", difficulty: "Medium",
            solutions: [
                {
                    label: "🔵 Classic backtracking",
                    tip: "Choose element, recurse with remaining, then un-choose (backtrack).",
                    code: `function permutations(nums) {
  const result  = [];

  function backtrack(current, remaining) {
    // Base case: no more elements to add
    if (!remaining.length) {
      result.push([...current]); // snapshot!
      return;
    }

    for (let i = 0; i < remaining.length; i++) {
      current.push(remaining[i]);          // CHOOSE
      backtrack(
        current,
        remaining.filter((_, j) => j !== i) // remaining without i
      );
      current.pop();                        // UN-CHOOSE
    }
  }

  backtrack([], nums);
  return result;
}

permutations([1,2,3]).length; // 6`,
                },
                {
                    label: "🟠 Heap's algorithm",
                    tip: "Most efficient permutation generator — avoids creating new arrays.",
                    code: `function permutations(arr) {
  const result = [];
  const a = [...arr];

  function generate(k) {
    if (k === 1) {
      result.push([...a]); // capture current permutation
      return;
    }
    generate(k - 1);

    for (let i = 0; i < k - 1; i++) {
      // Swap based on parity
      if (k % 2 === 0) [a[i], a[k-1]] = [a[k-1], a[i]];
      else             [a[0], a[k-1]] = [a[k-1], a[0]];
      generate(k - 1);
    }
  }

  generate(a.length);
  return result;
}`,
                },
                {
                    label: "🟢 Fibonacci with memoization",
                    tip: "Cache results in a Map to convert O(2ⁿ) → O(n). Top-down dynamic programming.",
                    code: `function fibonacci(n, memo = new Map()) {
  // Base cases
  if (n <= 1) return n;

  // Return cached result if computed before
  if (memo.has(n)) return memo.get(n);

  // Compute and CACHE before returning
  const result = fibonacci(n - 1, memo) + fibonacci(n - 2, memo);
  memo.set(n, result);
  return result;
}

fibonacci(10);  // 55
fibonacci(50);  // 12586269025 (instant with memo!)
// Without memo: 2^50 ≈ 10^15 calls — impossible`,
                },
            ],
        },
    ],
};

/**
 * ALL_TOPICS — master list driving the sidebar.
 * Order here = order in the navigation.
 * To add a new topic: add one object here + its data above.
 */
const ALL_TOPICS = [
    { id: "array",      label: "Array",       data: ARRAY_DATA,       icon: <ListAltIcon /> },
    { id: "set",        label: "Set",         data: SET_DATA,          icon: <FilterListIcon /> },
    { id: "map",        label: "Map",         data: MAP_DATA,          icon: <GridOnIcon /> },
    { id: "weakmap",    label: "WeakMap",     data: WEAK_MAP_DATA,     icon: <LayersIcon /> },
    { id: "weakset",    label: "WeakSet",     data: WEAK_SET_DATA,     icon: <StorageIcon /> },
    { id: "object",     label: "Object",      data: OBJECT_DATA,       icon: <AccountTreeIcon /> },
    { id: "linkedlist", label: "LinkedList",  data: LINKED_LIST_DATA,  icon: <LinkIcon /> },
    { id: "stack",      label: "Stack",       data: STACK_DATA,        icon: <SwapVertIcon /> },
    { id: "algorithms", label: "Algorithms",  data: ALGORITHMS_DATA,   icon: <FunctionsIcon /> },
];

// Sidebar width on desktop (pixels)
const DRAWER_WIDTH = 220;

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  SECTION 3 — SMALL REUSABLE COMPONENTS
//  Single Responsibility: each component does exactly ONE job.
//  Props-only coupling: components don't import from each other.
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * ComplexityBadge
 * Shows one operation + its complexity, colour-coded by speed.
 *
 * @param {string} operation - e.g. "get"
 * @param {string} value     - e.g. "O(1)"
 */
const ComplexityBadge = ({ operation, value }) => {
    // Colour logic: green = constant, amber = logarithmic, red = linear+
    const getColor = (v) => {
        if (v.includes("1)"))    return APP_COLORS.success;
        if (v.includes("log"))   return APP_COLORS.warning;
        if (v.includes("varies") || v === "N/A") return APP_COLORS.textMuted;
        return APP_COLORS.danger;
    };

    return (
        <Box sx={{
            textAlign: "center",
            border: `1px solid ${APP_COLORS.border}`,
            borderRadius: 1.5, px: 1.5, py: 0.5, minWidth: 64,
            bgcolor: "rgba(0,0,0,0.3)",
        }}>
            <Typography variant="caption" sx={{ color: APP_COLORS.textMuted, display: "block", fontSize: 9, textTransform: "uppercase" }}>
                {operation}
            </Typography>
            <Typography variant="caption" sx={{ color: getColor(value), fontWeight: 700, fontSize: 11 }}>
                {value}
            </Typography>
        </Box>
    );
};

/**
 * CategoryChip — colour-coded badge for a method category.
 * @param {string} category - key in CATEGORY_STYLE_MAP
 */
const CategoryChip = ({ category }) => {
    const style = CATEGORY_STYLE_MAP[category] || { bg: "#1e2d3d", color: "#8b949e" };
    return (
        <Chip label={category} size="small" sx={{
            bgcolor: style.bg, color: style.color,
            fontWeight: 700, fontSize: 9, height: 20,
            "& .MuiChip-label": { px: 1 },
        }} />
    );
};

/**
 * CopyButton — clipboard copy with a temporary check icon.
 *
 * @param {string} textToCopy
 * @param {string} copyId     - unique id for this button
 * @param {string} copiedId   - id of the most recently copied button
 * @param {fn}     onCopy     - called with copyId after copying
 */
const CopyButton = ({ textToCopy, copyId, copiedId, onCopy }) => {
    const isCopied = copiedId === copyId;
    return (
        <Tooltip title={isCopied ? "Copied!" : "Copy"}>
            <IconButton size="small" onClick={() => { navigator.clipboard.writeText(textToCopy); onCopy(copyId); }}
                        sx={{ color: isCopied ? APP_COLORS.success : APP_COLORS.textMuted, "&:hover": { color: APP_COLORS.title }, transition: "color 0.2s" }}>
                {isCopied ? <CheckIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
            </IconButton>
        </Tooltip>
    );
};

/**
 * CodeBlock — styled pre element with a floating copy button.
 */
const CodeBlock = ({ code, copyId, copiedId, onCopy }) => (
    <Box sx={{ position: "relative", mt: 1 }}>
        <Box sx={{ position: "absolute", top: 4, right: 4, zIndex: 1 }}>
            <CopyButton textToCopy={code} copyId={copyId} copiedId={copiedId} onCopy={onCopy} />
        </Box>
        <Box component="pre" sx={{
            bgcolor: APP_COLORS.codeBg,
            border: `1px solid ${APP_COLORS.border}`,
            borderRadius: 1.5,
            p: "12px 36px 12px 14px",
            fontSize: 11, lineHeight: 1.85,
            color: APP_COLORS.text,
            overflowX: "auto", m: 0,
            whiteSpace: "pre",
            fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
        }}>
            {code}
        </Box>
    </Box>
);

/**
 * MethodCard — displays one method: name, chip, description, code.
 */
const MethodCard = ({ method, color, cardIndex, copiedId, onCopy }) => (
    <Card sx={{
        bgcolor: APP_COLORS.surface,
        border: `1px solid ${APP_COLORS.border}`,
        borderRadius: 2,
        transition: "border-color 0.2s, transform 0.15s",
        "&:hover": { borderColor: color, transform: "translateY(-1px)" },
    }}>
        <CardContent sx={{ p: "12px !important" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                <Typography variant="caption" sx={{ color, fontWeight: 700, fontSize: 12, fontFamily: "monospace" }}>
                    {method.name}
                </Typography>
                <CategoryChip category={method.cat} />
            </Box>
            <Typography variant="caption" sx={{ color: APP_COLORS.textMuted, display: "block", mb: 0.75, lineHeight: 1.5, fontSize: 10.5 }}>
                {method.desc}
            </Typography>
            <CodeBlock code={method.code} copyId={`method-${cardIndex}`} copiedId={copiedId} onCopy={onCopy} />
        </CardContent>
    </Card>
);

/**
 * SolutionPanel — one practice problem with 3 solution tabs
 * and a collapsible "when to use each" comparison section.
 */
const SolutionPanel = ({ question, color, copiedId, onCopy }) => {
    const [activeSolution, setActiveSolution] = useState(0);
    const diffColor   = DIFFICULTY_COLOR_MAP[question.difficulty] || APP_COLORS.textMuted;
    const currentSol  = question.solutions[activeSolution];

    return (
        <Card sx={{ bgcolor: APP_COLORS.surface, border: `1px solid ${color}44`, borderRadius: 2, overflow: "hidden" }}>
            <CardContent sx={{ p: "0 !important" }}>

                {/* Problem title */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: "12px 16px", bgcolor: "rgba(0,0,0,0.3)", borderBottom: `1px solid ${APP_COLORS.border}` }}>
                    <CodeIcon sx={{ color, fontSize: 18 }} />
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 13, color: APP_COLORS.text }}>{question.title}</Typography>
                        <Typography sx={{ fontSize: 10, color: diffColor }}>● {question.difficulty} · 3 solutions</Typography>
                    </Box>
                </Box>

                {/* Solution selector */}
                <Tabs value={activeSolution} onChange={(_, v) => setActiveSolution(v)}
                      sx={{
                          bgcolor: "rgba(0,0,0,0.2)", borderBottom: `1px solid ${APP_COLORS.border}`, minHeight: 38,
                          "& .MuiTab-root": { minHeight: 38, fontSize: 10, fontWeight: 600, color: APP_COLORS.textMuted },
                          "& .Mui-selected": { color: `${color} !important` },
                          "& .MuiTabs-indicator": { bgcolor: color },
                      }}>
                    {question.solutions.map((s, i) => <Tab key={i} label={s.label} />)}
                </Tabs>

                {/* Strategy tip */}
                <Box sx={{ p: "10px 16px", bgcolor: `${color}0d`, borderBottom: `1px solid ${APP_COLORS.border}` }}>
                    <Typography sx={{ fontSize: 11, color: APP_COLORS.text }}>
                        <span style={{ color, fontWeight: 700 }}>💬 Strategy: </span>
                        {currentSol.tip}
                    </Typography>
                </Box>

                {/* Code block */}
                <Box sx={{ p: 2 }}>
                    <CodeBlock code={currentSol.code} copyId={`sol-${question.title}-${activeSolution}`} copiedId={copiedId} onCopy={onCopy} />
                </Box>

                {/* When to use accordion */}
                <Accordion disableGutters sx={{ bgcolor: "transparent", boxShadow: "none", borderTop: `1px solid ${APP_COLORS.border}`, "&:before": { display: "none" } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: APP_COLORS.textMuted, fontSize: 16 }} />} sx={{ px: 2, py: 0.5, minHeight: 36 }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: APP_COLORS.textMuted, letterSpacing: "0.06em" }}>
                            ⚖️ WHEN TO USE EACH APPROACH
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                        {question.solutions.map((sol, i) => (
                            <Box key={i} sx={{ display: "flex", gap: 1.5, mb: i < 2 ? 1.5 : 0 }}>
                                <Typography sx={{ fontSize: 14, minWidth: 22 }}>{["🔵","🟠","🟢"][i]}</Typography>
                                <Box>
                                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: APP_COLORS.text }}>{sol.label.replace(/^[^ ]+ /,"")}</Typography>
                                    <Typography sx={{ fontSize: 10, color: APP_COLORS.textMuted, mt: 0.25 }}>{sol.tip}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </AccordionDetails>
                </Accordion>

            </CardContent>
        </Card>
    );
};

/**
 * InfoBar — collection header: title, badge, declaration, complexity, note.
 */
const InfoBar = ({ topicData }) => (
    <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography sx={{ fontSize: 22 }}>{topicData.icon}</Typography>
            <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 900, color: APP_COLORS.title }}>{topicData.badge}</Typography>
                <Typography sx={{ fontSize: 11, color: APP_COLORS.textMuted }}>{topicData.tagline}</Typography>
            </Box>
        </Box>
        <Box sx={{ display: "inline-block", bgcolor: "rgba(0,0,0,0.4)", border: `1px solid ${APP_COLORS.border}`, borderRadius: 1, px: 1.5, py: 0.5, mb: 1.5 }}>
            <Typography component="code" sx={{ fontSize: 11, color: topicData.color, fontFamily: "monospace" }}>{topicData.declare}</Typography>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "flex-start" }}>
            {Object.entries(topicData.complexity).map(([op, val]) => (
                <ComplexityBadge key={op} operation={op} value={val} />
            ))}
            <Box sx={{ flex: 1, minWidth: 180, bgcolor: `${topicData.color}10`, border: `1px solid ${topicData.color}33`, borderRadius: 1.5, px: 1.5, py: 0.75 }}>
                <Typography sx={{ fontSize: 10, color: topicData.color, fontWeight: 700 }}>💡 Use when:</Typography>
                <Typography sx={{ fontSize: 10.5, color: APP_COLORS.text }}>{topicData.whenToUse}</Typography>
            </Box>
        </Box>
        {topicData.note && (
            <Typography sx={{ fontSize: 10, color: APP_COLORS.textMuted, mt: 0.75, fontStyle: "italic" }}>
                * {topicData.note}
            </Typography>
        )}
    </Box>
);

/**
 * MethodsTab — filterable grid of method cards.
 */
const MethodsTab = ({ topicData, copiedId, onCopy }) => {
    const [activeCategory, setActiveCategory] = useState("All");

    // Derive category list once — memo avoids recomputing every render
    const categories = useMemo(
        () => ["All", ...new Set(topicData.methods.map(m => m.cat))],
        [topicData.methods]
    );

    const visibleMethods = useMemo(
        () => activeCategory === "All" ? topicData.methods : topicData.methods.filter(m => m.cat === activeCategory),
        [topicData.methods, activeCategory]
    );

    if (!topicData.methods.length) return (
        <Box sx={{ textAlign: "center", py: 6, color: APP_COLORS.textMuted }}>
            <SchoolIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
            <Typography sx={{ fontSize: 13 }}>No methods listed — see Practice tab for algorithm patterns.</Typography>
        </Box>
    );

    return (
        <Box>
            {/* Category filter chips */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2.5 }}>
                {categories.map(cat => (
                    <Chip key={cat} label={cat} onClick={() => setActiveCategory(cat)}
                          variant={activeCategory === cat ? "filled" : "outlined"} size="small"
                          sx={{
                              cursor: "pointer", fontSize: 10, fontWeight: 600,
                              color: activeCategory === cat ? APP_COLORS.background : APP_COLORS.textMuted,
                              bgcolor: activeCategory === cat ? topicData.color : "transparent",
                              borderColor: activeCategory === cat ? topicData.color : APP_COLORS.border,
                              "&:hover": { borderColor: topicData.color, color: topicData.color },
                          }}
                    />
                ))}
            </Box>

            {/* Responsive grid — auto-fill, min 260px columns */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 1.5 }}>
                {visibleMethods.map((method, idx) => (
                    <MethodCard key={method.name} method={method} color={topicData.color}
                                cardIndex={`${topicData.badge}-${idx}`} copiedId={copiedId} onCopy={onCopy} />
                ))}
            </Box>
        </Box>
    );
};

/**
 * PracticeTab — question selector + solution panel.
 */
const PracticeTab = ({ topicData, copiedId, onCopy }) => {
    const [activeQuestion, setActiveQuestion] = useState(0);

    return (
        <Box>
            {/* Question selector */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                {topicData.questions.map((q, idx) => {
                    const isActive   = activeQuestion === idx;
                    const diffColor  = DIFFICULTY_COLOR_MAP[q.difficulty] || APP_COLORS.textMuted;
                    return (
                        <Button key={idx} onClick={() => setActiveQuestion(idx)}
                                variant={isActive ? "contained" : "outlined"} size="small"
                                sx={{
                                    textTransform: "none", fontSize: 11, fontWeight: 700, fontFamily: "monospace",
                                    borderColor: isActive ? topicData.color : APP_COLORS.border,
                                    bgcolor: isActive ? `${topicData.color}22` : "transparent",
                                    color: isActive ? topicData.color : APP_COLORS.textMuted,
                                    "&:hover": { borderColor: topicData.color, bgcolor: `${topicData.color}11` },
                                    display: "flex", flexDirection: "column", alignItems: "flex-start",
                                    px: 1.5, py: 0.75, lineHeight: 1.4,
                                }}>
                            <span>#{idx + 1} {q.title}</span>
                            <span style={{ fontSize: 9, color: diffColor }}>● {q.difficulty}</span>
                        </Button>
                    );
                })}
            </Box>
            {topicData.questions[activeQuestion] && (
                <SolutionPanel question={topicData.questions[activeQuestion]}
                               color={topicData.color} copiedId={copiedId} onCopy={onCopy} />
            )}
        </Box>
    );
};

/**
 * TopicContent — Methods/Practice tab switcher for one topic.
 * key={activeTopicId} on this component resets it when topic changes.
 */
const TopicContent = ({ topicData, copiedId, onCopy }) => {
    const [activeTab, setActiveTab] = useState(
        // Auto-start on Practice tab if topic has no methods
        topicData.methods.length === 0 ? 1 : 0
    );

    return (
        <Box>
            <InfoBar topicData={topicData} />
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
                  sx={{
                      mb: 3, borderBottom: `1px solid ${APP_COLORS.border}`,
                      "& .MuiTab-root": { fontSize: 11, fontWeight: 700, color: APP_COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", minHeight: 40 },
                      "& .Mui-selected": { color: `${topicData.color} !important` },
                      "& .MuiTabs-indicator": { bgcolor: topicData.color },
                  }}>
                <Tab label="📋 Methods" disabled={topicData.methods.length === 0} />
                <Tab label="🧩 Practice" />
            </Tabs>
            {activeTab === 0 && <MethodsTab topicData={topicData} copiedId={copiedId} onCopy={onCopy} />}
            {activeTab === 1 && <PracticeTab topicData={topicData} copiedId={copiedId} onCopy={onCopy} />}
        </Box>
    );
};

/**
 * SidebarContent — navigation list rendered inside the Drawer.
 * @param {string} activeTopicId
 * @param {fn}     onSelect      - called with topicId on click
 */
const SidebarContent = ({ activeTopicId, onSelect }) => (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>

        {/* App title */}
        <Box sx={{ p: 2, pb: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <JavascriptIcon sx={{ color: APP_COLORS.jsYellow, fontSize: 26 }} />
                <Typography sx={{ fontWeight: 900, fontSize: 12, color: APP_COLORS.title, lineHeight: 1.2 }}>
                    JS Complete
                </Typography>
            </Box>
            <Typography sx={{ fontSize: 10, color: APP_COLORS.textMuted, pl: "30px" }}>
                Algorithms
            </Typography>
        </Box>

        <Divider sx={{ borderColor: APP_COLORS.border }} />

        {/* Navigation items */}
        <List dense sx={{ flex: 1, py: 1, overflowY: "auto" }}>
            {ALL_TOPICS.map((topic) => {
                const isActive = activeTopicId === topic.id;
                return (
                    <ListItem key={topic.id} disablePadding>
                        <ListItemButton onClick={() => onSelect(topic.id)} sx={{
                            borderRadius: 1.5, mx: 1, my: 0.25,
                            bgcolor: isActive ? `${topic.data.color}22` : "transparent",
                            borderLeft: isActive ? `3px solid ${topic.data.color}` : "3px solid transparent",
                            "&:hover": { bgcolor: `${topic.data.color}11` },
                            transition: "all 0.18s", py: 0.75,
                        }}>
                            <ListItemIcon sx={{ minWidth: 32, color: isActive ? topic.data.color : APP_COLORS.textMuted }}>
                                {topic.data.icon}
                            </ListItemIcon>
                            <ListItemText primary={topic.label} primaryTypographyProps={{
                                fontSize: 12, fontWeight: isActive ? 700 : 400,
                                color: isActive ? topic.data.color : APP_COLORS.text,
                            }} />
                            {isActive && <BookmarkIcon sx={{ fontSize: 12, color: topic.data.color }} />}
                        </ListItemButton>
                    </ListItem>
                );
            })}
        </List>

        <Divider sx={{ borderColor: APP_COLORS.border }} />

        {/* Footer */}
        <Box sx={{ p: 1.5, textAlign: "center" }}>
            <Typography sx={{ fontSize: 9, color: APP_COLORS.textMuted }}>
                🟡 JavaScript Complete Algorithms
            </Typography>
            <Typography sx={{ fontSize: 9, color: APP_COLORS.textMuted }}>
                8 Structures · 6 Algorithm Patterns
            </Typography>
        </Box>
    </Box>
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  SECTION 4 — ROOT APP COMPONENT
//  Manages global state only:
//    • Active topic
//    • Mobile drawer open/closed
//    • Which code block is "copied"
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * AppContent — layout shell: AppBar (mobile) + Drawer + main area.
 * Separated from the root so the theme is active before this mounts.
 */
const AppContent = () => {
    const theme    = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    // ── State ─────────────────────────────────────────────────
    const [activeTopicId,    setActiveTopicId]    = useState("array");
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [copiedId,         setCopiedId]         = useState(null);

    // Find active topic data from the master list
    const activeTopic = ALL_TOPICS.find(t => t.id === activeTopicId);

    /**
     * handleTopicSelect — selects a topic and closes the mobile drawer.
     */
    const handleTopicSelect = (topicId) => {
        setActiveTopicId(topicId);
        if (isMobile) setMobileDrawerOpen(false); // auto-close on mobile
    };

    /**
     * handleCopy — sets copiedId and auto-clears after 1.5 s.
     */
    const handleCopy = (id) => {
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 1500);
    };

    // Desktop = permanent drawer; Mobile = temporary (slide-in)
    const drawerContent = (
        <SidebarContent activeTopicId={activeTopicId} onSelect={handleTopicSelect} />
    );

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: APP_COLORS.background }}>

            {/* ── Mobile AppBar ── */}
            {isMobile && (
                <AppBar position="fixed" sx={{
                    bgcolor: APP_COLORS.drawerBg, borderBottom: `1px solid ${APP_COLORS.border}`,
                    zIndex: theme.zIndex.drawer + 1, boxShadow: "none",
                }}>
                    <Toolbar variant="dense" sx={{ gap: 1, minHeight: 48 }}>
                        <IconButton edge="start" size="small" onClick={() => setMobileDrawerOpen(true)} sx={{ color: APP_COLORS.jsYellow }}>
                            <MenuIcon />
                        </IconButton>
                        <Typography sx={{ fontWeight: 700, fontSize: 13, color: APP_COLORS.title, flex: 1 }}>
                            {activeTopic?.label ?? "JS Algorithms"}
                        </Typography>
                        <Typography sx={{ fontSize: 18 }}>{activeTopic?.data.icon}</Typography>
                    </Toolbar>
                </AppBar>
            )}

            {/* ── Desktop permanent sidebar ── */}
            {!isMobile && (
                <Drawer variant="permanent" sx={{
                    width: DRAWER_WIDTH, flexShrink: 0,
                    "& .MuiDrawer-paper": {
                        width: DRAWER_WIDTH, bgcolor: APP_COLORS.drawerBg,
                        borderRight: `1px solid ${APP_COLORS.border}`, overflowX: "hidden",
                    },
                }}>
                    {drawerContent}
                </Drawer>
            )}

            {/* ── Mobile temporary drawer ── */}
            {isMobile && (
                <Drawer variant="temporary" open={mobileDrawerOpen}
                        onClose={() => setMobileDrawerOpen(false)}
                        ModalProps={{ keepMounted: true }}
                        sx={{ "& .MuiDrawer-paper": { width: DRAWER_WIDTH, bgcolor: APP_COLORS.drawerBg, borderRight: `1px solid ${APP_COLORS.border}` } }}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", p: 0.5 }}>
                        <IconButton size="small" onClick={() => setMobileDrawerOpen(false)} sx={{ color: APP_COLORS.textMuted }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    {drawerContent}
                </Drawer>
            )}

            {/* ── Main content area ── */}
            <Box component="main" sx={{
                flexGrow: 1,
                mt: isMobile ? "48px" : 0,
                minWidth: 0, // prevent flex overflow
                overflowX: "hidden",
            }}>
                <Box sx={{ maxWidth: 1100, mx: "auto", p: { xs: 2, sm: 3 } }}>

                    {/* Desktop header */}
                    {!isMobile && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, pb: 2, borderBottom: `1px solid ${APP_COLORS.border}` }}>
                            <Typography sx={{ fontSize: 24 }}>{activeTopic?.data.icon}</Typography>
                            <Box>
                                <Typography sx={{ fontWeight: 900, fontSize: 18, color: APP_COLORS.title }}>
                                    {activeTopic?.label}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: APP_COLORS.textMuted }}>
                                    JavaScript Complete Algorithms — Data Structures &amp; Algorithm Patterns
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/*
           * key={activeTopicId} forces React to UNMOUNT + REMOUNT TopicContent
           * whenever the topic changes — this resets all internal tab/filter
           * state automatically, without needing manual reset handlers.
           */}
                    {activeTopic && (
                        <TopicContent
                            key={activeTopicId}
                            topicData={activeTopic.data}
                            copiedId={copiedId}
                            onCopy={handleCopy}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );
};

/**
 * JavaScriptCompleteAlgorithms — ROOT EXPORT
 * Wraps everything in MUI ThemeProvider + CssBaseline.
 * useMemo prevents rebuilding the theme on every render.
 */
 function JavaScriptCompleteAlgorithms() {
    const appTheme = useMemo(() => buildAppTheme(), []);

    return (
        <ThemeProvider theme={appTheme}>
            {/* CssBaseline: CSS reset + applies theme background to <body> */}
            <CssBaseline />
            <AppContent />
        </ThemeProvider>
    );
}

export default JavaScriptCompleteAlgorithms
